import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/* --------------------------------- tokens ---------------------------------- */

const T = {
  ink: "#000000", ink2: "#5A5A5A", ink3: "#8C8C8C", ink4: "#BFBFBF",
  hair: "#DCDCDC", hairSoft: "#EDEDED", wash: "#F5F5F5", surface: "#FFFFFF",
};
const sans = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif`;
const mono = `ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace`;
const MIN = 60000;
const AGO = (m) => Date.now() - m * MIN;

/* ------------------------------- staff, roles ------------------------------ */

const STAFF = [
  { id: "s1", name: "Renee Alcaraz", role: "Dispatch", phone: "(713) 555-0170", hours: "Mon–Fri, 6:00a–6:00p CT", onHours: true, can: ["ops"] },
  { id: "s2", name: "Curtis Mabry", role: "After hours", phone: "(713) 555-0184", hours: "Nights and weekends", onHours: false, can: ["ops"] },
  { id: "s3", name: "Priya Raghunathan", role: "Billing and advances", phone: "(713) 555-0192", hours: "Mon–Fri, 8:00a–5:00p CT", onHours: true, can: ["billing"] },
  { id: "s4", name: "Hollis Grant", role: "Operations manager", phone: "(713) 555-0155", hours: "Any time", onHours: true, can: ["ops", "billing", "safety"] },
];

/* who the person at the keyboard is — drives what they're allowed to clear */
const SEATS = [
  { id: "s1", label: "Renee — Dispatch" },
  { id: "s3", label: "Priya — Billing" },
  { id: "s4", label: "Hollis — Ops manager" },
];

/* ------------------------------- drivers, ELD ------------------------------ */

const SEED_DRIVERS = [
  { id: "d1", name: "Marcus Ellison", phone: "(713) 555-0142", truck: "T-118", trailer: "53' dry van", language: "English", baseLanguage: "English", tz: "CT", tzShift: 0, quiet: "9:00p–6:00a", canRequest: true,
    attempts: { first: 168, second: 31, third: 15 }, avgAnswerSec: 14,
    eld: { provider: "Motive", inMotion: true, position: "I-10 near Winnie, TX", speed: 64, driveLeft: 3.2, dutyLeft: 5.1, updated: 2 },
    note: "Prefers calls after 7am. Reliable on ETA estimates." },
  { id: "d2", name: "Rosa Villareal", phone: "(956) 555-0188", truck: "T-204", trailer: "53' reefer", language: "Spanish / English", baseLanguage: "Spanish / English", tz: "CT", tzShift: 0, quiet: "10:00p–5:00a", canRequest: true,
    attempts: { first: 131, second: 24, third: 8 }, avgAnswerSec: 21,
    eld: { provider: "Motive", inMotion: false, position: "Victoria, TX — stopped 8 min", speed: 0, driveLeft: 5.8, dutyLeft: 7.4, updated: 1 },
    note: "Runs the Laredo lane. Alfred switches to Spanish automatically." },
  { id: "d3", name: "Dwight Okafor", phone: "(281) 555-0119", truck: "T-090", trailer: "48' flatbed", language: "English", baseLanguage: "English", tz: "CT", tzShift: 0, quiet: "9:00p–6:00a", canRequest: true,
    attempts: { first: 41, second: 29, third: 27 }, avgAnswerSec: 48,
    eld: { provider: "Samsara", inMotion: false, position: "Port Allen Depot, LA — on site", speed: 0, driveLeft: 2.1, dutyLeft: 3.0, updated: 4 },
    note: "Rarely answers the first attempt. Second try at +20 min usually connects." },
  { id: "d4", name: "Tomás Aguilar", phone: "(832) 555-0106", truck: "T-231", trailer: "53' dry van", language: "Spanish / English", baseLanguage: "Spanish / English", tz: "PT", tzShift: -2, quiet: "9:00p–6:00a", canRequest: true,
    attempts: { first: 96, second: 12, third: 3 }, avgAnswerSec: 11,
    eld: { provider: "Samsara", inMotion: true, position: "I-10 near Columbus, TX", speed: 68, driveLeft: 6.4, dutyLeft: 8.0, updated: 1 },
    note: "Based in Ontario, CA — runs Pacific time. Fastest pickup on the fleet." },
  { id: "d5", name: "Wendell Boyce", phone: "(409) 555-0175", truck: "T-055", trailer: "53' dry van", language: "English", baseLanguage: "English", tz: "CT", tzShift: 0, quiet: "10:00p–6:00a", canRequest: false,
    attempts: { first: 88, second: 11, third: 6 }, avgAnswerSec: 17,
    eld: { provider: "Motive", inMotion: false, position: "Houston yard — off duty", speed: 0, driveLeft: 11.0, dutyLeft: 14.0, updated: 6 },
    note: "Available for relay. Lives twenty minutes from the yard." },
];

const UPDATE_VIA = {
  call: "Phone call", macropoint: "MacroPoint tracking", truckertools: "Trucker Tools",
  email: "Email only", portal: "Their portal",
};

/* ---------------------- stop-aware call plan generation --------------------- */

/**
 * Builds the plan from the stop list, so a 2-pick 1-drop load gets
 * arrive/loaded steps per pickup rather than one generic "at the shipper".
 */
function buildPlan(stops) {
  const p = [
    { id: "assign", party: "driver", title: "Load assignment", detail: "Brief the driver on the load, confirm they accept it, read back the first pickup address and appointment." },
    { id: "truckinfo", party: "broker", title: "Truck and driver details", detail: "Truck number, trailer, driver name and cell." },
    { id: "yard", party: "driver", title: "Left the yard?", detail: "Confirm the driver is rolling toward the first stop and get an ETA." },
    { id: "etapickup", party: "broker", title: "Departed for pickup", detail: "Departure time and ETA to the first shipper." },
  ];
  const pickups = stops.filter((s) => s.type === "pickup");
  const drops = stops.filter((s) => s.type === "drop");

  stops.forEach((s) => {
    if (s.type === "pickup") {
      const nth = pickups.length > 1 ? ` (pickup ${pickups.indexOf(s) + 1} of ${pickups.length})` : "";
      p.push({ id: `arrive${s.seq}`, stopSeq: s.seq, party: "driver", title: `Arrived at ${s.name}${nth}`, detail: `Confirm gate-in at ${s.name}, start the detention clock, ask about dock delay.`, captures: ["Gate-in time", "Dock"] });
      p.push({ id: `loaded${s.seq}`, stopSeq: s.seq, party: "broker", title: `Loaded at ${s.name}`, detail: "Read back BOL and seal numbers and weight, then pass them to the broker with an ETA to the next stop.", captures: ["BOL number", "Seal number", "Weight"] });
    }
  });

  p.push({ id: "enroute", party: "driver", title: "En route check call", detail: "Position, hours remaining, anything needed." });

  stops.forEach((s) => {
    if (s.type === "drop") {
      const nth = drops.length > 1 ? ` (drop ${drops.indexOf(s) + 1} of ${drops.length})` : "";
      p.push({ id: `arrive${s.seq}`, stopSeq: s.seq, party: "driver", title: `Arrived at ${s.name}${nth}`, detail: `Confirm arrival and unload status at ${s.name}. Start the detention clock if they're held.`, captures: ["Gate-in time"] });
      p.push({ id: `pod${s.seq}`, stopSeq: s.seq, party: "driver", title: `Unloaded at ${s.name}`, detail: "Confirm who signed the POD and at what time, and whether anything was noted on it.", captures: ["POD signer", "POD time", "Exceptions noted"] });
    }
  });

  p.push({ id: "delivered", party: "broker", title: "Delivered", detail: "POD details, detention, and empty status." });
  p.push({ id: "empty", party: "driver", title: "Empty and available", detail: "Confirm the driver is empty, where they're sitting, and what hours they have left for a reload.", captures: ["Empty location", "Hours available"] });

  return p.map((s) => ({ ...s, at: "", state: "scheduled", notes: "", extra: false, captures: s.captures || [] }));
}

const applyPlan = (stops, over) =>
  buildPlan(stops).map((s) => ({ ...s, ...(over[s.id] || {}) }));

/* ---------------------------------- loads ---------------------------------- */

const L1_STOPS = [
  { seq: 1, type: "pickup", name: "Gulf Coast Terminal", city: "Houston, TX", window: "Today 08:00–11:00", windowEnd: "11:00 AM", state: "complete", arrivedAt: AGO(310) },
  { seq: 2, type: "drop", name: "Rio Grande DC", city: "Laredo, TX", window: "Today 20:00–23:00", windowEnd: "11:00 PM", state: "pending", etaAt: "8:40 PM" },
];
const L2_STOPS = [
  { seq: 1, type: "pickup", name: "Baytown Chemical", city: "Baytown, TX", window: "Today 13:00–15:00", windowEnd: "3:00 PM", state: "pending", etaAt: "1:40 PM" },
  { seq: 2, type: "pickup", name: "Channelview Annex", city: "Channelview, TX", window: "Today 15:30–16:30", windowEnd: "4:30 PM", state: "pending", etaAt: "5:10 PM" },
  { seq: 3, type: "drop", name: "Trinity Crossdock", city: "Dallas, TX", window: "Tomorrow 06:00–09:00", windowEnd: "9:00 AM", state: "pending", etaAt: "7:30 AM" },
];
const L3_STOPS = [
  { seq: 1, type: "pickup", name: "Pasadena Yard 4", city: "Pasadena, TX", window: "Today 06:00–08:00", windowEnd: "8:00 AM", state: "complete", arrivedAt: AGO(520) },
  { seq: 2, type: "drop", name: "Port Allen Depot", city: "New Orleans, LA", window: "Today 18:00–21:00", windowEnd: "9:00 PM", state: "arrived", arrivedAt: AGO(148) },
];
const L4_STOPS = [
  { seq: 1, type: "pickup", name: "Katy Freeway Cold", city: "Katy, TX", window: "Today 11:00–13:00", windowEnd: "1:00 PM", state: "complete", arrivedAt: AGO(200) },
  { seq: 2, type: "drop", name: "Bandera Foods", city: "San Antonio, TX", window: "Today 19:00–21:00", windowEnd: "9:00 PM", state: "pending", etaAt: "7:15 PM" },
];
const L5_STOPS = [
  { seq: 1, type: "pickup", name: "Deer Park Terminal", city: "Deer Park, TX", window: "Yesterday 14:00–16:00", windowEnd: "4:00 PM", state: "canceled" },
  { seq: 2, type: "drop", name: "Beaumont Yard", city: "Beaumont, TX", window: "Yesterday 19:00–21:00", windowEnd: "9:00 PM", state: "canceled" },
];

const SEED_LOADS = [
  {
    id: "l1", ref: "HOU-44812", status: "rolling", rate: "$2,450", source: "Rate confirmation",
    stops: L1_STOPS, driverId: "d2", contact1: "s1", contact2: "s2", hoursToNextStop: 4.0,
    broker: { name: "Ridgeline Logistics", contact: "Dana Whitfield", phone: "(469) 555-0177", updateVia: "call", cadence: "Every 4 hours" },
    paperwork: [
      { k: "BOL number", v: "GC-77413", at: "10:06 AM" },
      { k: "Seal number", v: "88214", at: "10:06 AM" },
      { k: "Weight", v: "41,300 lbs", at: "10:06 AM" },
      { k: "POD signer", v: null }, { k: "POD time", v: null },
    ],
    captured: { location: "Victoria, TX", eta: "8:40 PM" },
    plan: applyPlan(L1_STOPS, {
      assign: { state: "done", at: "6:28 AM" },
      truckinfo: { state: "done", at: "6:44 AM", notes: "Ridgeline has T-204 and Rosa's cell on file." },
      yard: { state: "done", at: "7:12 AM" },
      etapickup: { state: "done", at: "7:31 AM" },
      arrive1: { state: "done", at: "8:22 AM", notes: "Gate-in 8:22, dock 14." },
      loaded1: { state: "done", at: "10:06 AM", notes: "Seal 88214, 41,300 lbs." },
      enroute: { state: "due", at: "2:00 PM" },
    }),
    activity: [{ t: "10:06 AM", head: "Loaded at Gulf Coast Terminal", body: "Seal and weight read back and confirmed, then passed to Ridgeline. ETA 8:40 PM." }],
  },
  {
    id: "l2", ref: "HOU-44815", status: "attention", rate: "$1,890", source: "TMS",
    stops: L2_STOPS, driverId: "d1", contact1: "s1", contact2: "s4", hoursToNextStop: 4.4,
    broker: { name: "Meridian Freight", contact: "Sam Oyelaran", phone: "(214) 555-0163", updateVia: "macropoint", cadence: "At pickup and delivery" },
    paperwork: [{ k: "BOL number", v: null }, { k: "Seal number", v: null }, { k: "Weight", v: null }, { k: "POD signer", v: null }],
    captured: null,
    plan: applyPlan(L2_STOPS, {
      assign: { state: "due", at: "11:15 AM", notes: "Confirm Marcus can make the 1pm window — his clock is tight." },
      truckinfo: { at: "11:30 AM" }, yard: { at: "11:45 AM" }, etapickup: { at: "12:00 PM" },
      arrive1: { at: "1:00 PM" }, loaded1: { at: "2:30 PM" },
      arrive2: { at: "3:45 PM" }, loaded2: { at: "4:30 PM" },
      enroute: { at: "6:00 PM" }, arrive3: { at: "6:30 AM" }, pod3: { at: "7:00 AM" },
      delivered: { at: "7:15 AM" }, empty: { at: "7:30 AM" },
    }),
    activity: [],
  },
  {
    id: "l3", ref: "HOU-44803", status: "attention", rate: "$3,120", source: "Rate confirmation",
    stops: L3_STOPS, driverId: "d3", contact1: "s3", contact2: "s2", hoursToNextStop: 0,
    broker: { name: "Bayou Bridge Brokers", contact: "Alicia Reyes", phone: "(504) 555-0131", updateVia: "email", cadence: "Every 4 hours" },
    paperwork: [
      { k: "BOL number", v: "PY-31882", at: "6:58 AM" }, { k: "Seal number", v: "40119", at: "6:58 AM" },
      { k: "Weight", v: "44,900 lbs", at: "6:58 AM" }, { k: "POD signer", v: null }, { k: "POD time", v: null },
    ],
    captured: { location: "Port Allen Depot, LA", eta: "Arrived" },
    plan: applyPlan(L3_STOPS, {
      assign: { state: "done", at: "5:40 AM" }, truckinfo: { state: "done", at: "5:55 AM" },
      yard: { state: "done", at: "6:20 AM" }, etapickup: { state: "done", at: "6:35 AM" },
      arrive1: { state: "done", at: "6:52 AM" }, loaded1: { state: "done", at: "7:45 AM" },
      enroute: { state: "done", at: "11:30 AM" }, arrive2: { state: "done", at: "1:35 PM" },
      pod2: { state: "due", at: "3:30 PM" },
    }),
    activity: [{ t: "1:35 PM", head: "Arrived at Port Allen Depot", body: "On site, waiting on a door. Detention clock started." }],
  },
  {
    id: "l4", ref: "HOU-44820", status: "rolling", rate: "$1,640", source: "Manual entry",
    stops: L4_STOPS, driverId: "d4", contact1: "s1", contact2: null, hoursToNextStop: 3.1,
    broker: { name: "Ridgeline Logistics", contact: "Dana Whitfield", phone: "(469) 555-0177", updateVia: "call", cadence: "Every 2 hours" },
    paperwork: [
      { k: "BOL number", v: "KF-20551", at: "11:42 AM" }, { k: "Seal number", v: "77302", at: "11:42 AM" },
      { k: "Weight", v: "38,100 lbs", at: "11:42 AM" }, { k: "POD signer", v: null },
    ],
    captured: { location: "Columbus, TX", eta: "7:15 PM" },
    plan: applyPlan(L4_STOPS, {
      assign: { state: "done", at: "9:05 AM" }, truckinfo: { state: "done", at: "9:20 AM" },
      yard: { state: "done", at: "10:10 AM" }, etapickup: { state: "done", at: "10:22 AM" },
      arrive1: { state: "done", at: "11:15 AM" }, loaded1: { state: "done", at: "11:42 AM" },
      enroute: { state: "due", at: "4:00 PM" },
    }),
    activity: [{ t: "11:42 AM", head: "Loaded at Katy Freeway Cold", body: "Sealed and rolling west on I-10." }],
  },
  {
    id: "l5", ref: "HOU-44799", status: "tonu", rate: "$350", source: "Rate confirmation",
    stops: L5_STOPS, driverId: "d3", contact1: "s3", contact2: null, hoursToNextStop: 0,
    broker: { name: "Meridian Freight", contact: "Sam Oyelaran", phone: "(214) 555-0163", updateVia: "email", cadence: "—" },
    paperwork: [], captured: null,
    plan: applyPlan(L5_STOPS, { assign: { state: "done", at: "1:10 PM" }, truckinfo: { state: "done", at: "1:22 PM" } }).slice(0, 2),
    activity: [{ t: "Yesterday 3:40 PM", head: "Load canceled at the shipper", body: "Product not ready. TONU claim filed with Meridian." }],
  },
];

/* --------------------------------- handoffs -------------------------------- */

const KIND = {
  incident: "Incident — person now",
  money: "Money",
  appointment: "Appointment change",
  concern: "Driver concern",
  unreachable: "Repeat callbacks",
};

/* money asks, so routing isn't guesswork */
const MONEY_KIND = {
  lumper: { label: "Lumper fee", to: "billing" },
  advance: { label: "Fuel advance", to: "billing" },
  layover: { label: "Layover", to: "ops" },
  detention: { label: "Detention", to: "billing" },
  scale: { label: "Scale ticket", to: "billing" },
  tarp: { label: "Tarp pay", to: "billing" },
  assist: { label: "Driver assist", to: "ops" },
};

const NEEDS = { incident: "safety", money: "billing", appointment: "ops", concern: "ops", unreachable: "ops" };

const SEED_QUEUE = [
  {
    id: "q0", kind: "incident", loadRef: "HOU-44820", driverId: "d4", waiting: 3, jumped: true,
    title: "Tire blowout on I-10, no injuries",
    quote: "Steer tire let go about a mile back. I'm on the shoulder, nobody's hurt, trailer looks fine.",
    ask: "Alfred stopped the call plan and is holding the line until a person picks up.",
    intake: [["Location", "I-10 westbound, MM 696, Columbus TX"], ["Injuries", "None reported"], ["Other vehicles", "None"], ["Cargo", "No visible damage, reefer running"], ["Police", "Not called"], ["Truck", "T-231, on the shoulder, hazards on"]],
  },
  { id: "q1", kind: "money", money: "lumper", loadRef: "HOU-44803", driverId: "d3", waiting: 14, amount: "$175",
    title: "Lumper fee at the receiver", quote: "They're telling me it's a hundred seventy-five to get unloaded and my card won't cover it.",
    ask: "Needs a Comdata code or a card authorization before the dock will take the trailer." },
  { id: "q2", kind: "appointment", loadRef: "HOU-44815", driverId: "d1", waiting: 31,
    title: "Won't make the 3:00 PM window at Channelview", quote: "If they load me past two I'm out of hours before Dallas.",
    ask: "ETA is 5:10 PM against a 4:30 PM close. Alfred flagged this before the window, not after. Needs a reset appointment or a relay." },
  { id: "q3", kind: "money", money: "advance", loadRef: "HOU-44812", driverId: "d2", waiting: 52, amount: "$200",
    title: "Fuel advance requested", quote: "I'm going to need an advance to make it to Laredo without stopping short.",
    ask: "Requires approval and a card load. Alfred does not move money." },
  { id: "q4", kind: "unreachable", loadRef: "HOU-44803", driverId: "d3", waiting: 96,
    title: "Three attempts, no answer", quote: "Voicemail on all three attempts, 20 minutes apart.",
    ask: "Next rung is the emergency contact on file." },
  { id: "q5", kind: "money", money: "detention", loadRef: "HOU-44803", driverId: "d3", waiting: 6, amount: "28 min and counting",
    title: "Detention past free time at Port Allen", quote: "Still no door. I've been on the property since one thirty-five.",
    ask: "Broker notified at the two-hour mark. Billing decides whether to invoice or eat it." },
];

const LADDER = [
  { at: 0, who: "Primary contact" }, { at: 20, who: "Second contact" },
  { at: 45, who: "Operations manager" }, { at: 90, who: "Emergency contact on file" },
];

const SEED_WELLBEING = [
  { id: "w1", driverId: "d3", at: "9:52 AM", loadRef: "HOU-44803", signal: "Time out and fatigue", quote: "I've been out twenty-two days straight. I'm running on fumes.", context: "Came up when Alfred asked about hours. Alfred didn't push — it logged this and moved on." },
  { id: "w2", driverId: "d1", at: "Yesterday, 6:10 PM", loadRef: "HOU-44790", signal: "Family situation", quote: "My mother's in the hospital and I'm eight hundred miles from home.", context: "Driver asked whether he could get routed toward Houston this weekend." },
  { id: "w3", driverId: "d4", at: "Aug 5", loadRef: "HOU-44771", signal: "Mentioned a rough stretch", quote: "Honestly the last few weeks have been rough on me.", context: "Renee called back the same afternoon.", handled: true },
];

const SEED_ERRANDS = [
  { id: "e1", driverId: "d2", at: "1:12 PM", state: "done", ask: "Find overnight parking near Laredo",
    calls: [{ place: "Love's #382, Laredo", result: "About 12 open spots, first come" }, { place: "Pilot Travel Center, Laredo", result: "Expects to be full after 6 PM" }, { place: "TA Laredo", result: "Reserved parking available, $18" }],
    reported: "Called Rosa back at 1:19 PM with all three." },
  { id: "e2", driverId: "d3", at: "10:30 AM", state: "done", ask: "Nearest certified scale open now",
    calls: [{ place: "CAT Scale, Love's Baytown", result: "Open 24 hours, $14.50 first weigh" }, { place: "Pasadena Public Scale", result: "Closed until Monday" }],
    reported: "Read the Baytown exit and address back to Dwight twice." },
  { id: "e3", driverId: "d4", at: "Now", state: "running", ask: "Road service for a blown steer tire on I-10",
    calls: [{ place: "Columbus Truck Tire", result: "On the line now" }, { place: "Bayou Tire and Service", result: "Queued" }], reported: null },
];

const CALL_LOG = [
  { id: "c1", day: "today", at: "10:06 AM", loadRef: "HOU-44812", driverId: "d2", party: "driver", step: "Loaded at Gulf Coast Terminal", dur: 96, outcome: "Completed",
    captured: ["BOL GC-77413", "Seal 88214", "41,300 lbs"],
    transcript: [{ who: "alfred", text: "Hi Rosa, Alfred again on 44812. Are you loaded and out of the gate?" }, { who: "driver", text: "Yeah, just pulled out. Seal is eight-eight-two-one-four." }, { who: "alfred", text: "Eight-eight-two-one-four. And the BOL number and weight off the paperwork?" }, { who: "driver", text: "G-C seven seven four one three, forty-one thousand three hundred." }, { who: "alfred", text: "Reading that back: BOL G-C-7-7-4-1-3, 41,300 pounds. Correct?" }, { who: "driver", text: "That's it." }] },
  { id: "c2", day: "today", at: "10:08 AM", loadRef: "HOU-44812", driverId: null, party: "broker", step: "Loaded and rolling", dur: 54, outcome: "Completed",
    captured: ["Broker notified", "Next check call 2:00 PM"],
    transcript: [{ who: "alfred", text: "Dana, Alfred with an update on 44812. Loaded and departed Houston at 10:04." }, { who: "broker", text: "Seal number?" }, { who: "alfred", text: "Eight-eight-two-one-four, 41,300 pounds. ETA Laredo 8:40 PM." }] },
  { id: "c6", day: "today", at: "1:22 PM", loadRef: "HOU-44820", driverId: "d4", party: "driver", step: "Incident — tire blowout", dur: 148, outcome: "Handed to contact",
    captured: ["No injuries", "MM 696 westbound", "Escalated to Hollis Grant"],
    transcript: [{ who: "driver", text: "Alfred, my steer tire just blew. I'm on the shoulder." }, { who: "alfred", text: "First — is anyone hurt?" }, { who: "driver", text: "No, I'm fine. Nobody else involved." }, { who: "alfred", text: "Good. Are you and the truck fully off the travel lane with hazards on?" }, { who: "driver", text: "Yeah, all the way over." }, { who: "alfred", text: "I'm getting a person on this line now. Stay in the cab, away from traffic. I'm not handling this one myself." }] },
  { id: "c3", day: "today", at: "9:14 AM", loadRef: "HOU-44803", driverId: "d3", party: "driver", step: "Left the yard?", dur: 41, outcome: "No answer",
    captured: ["Attempt 3 of 3", "Voicemail left", "Escalated to Priya Raghunathan"],
    transcript: [{ who: "alfred", text: "Voicemail: Dwight, this is Alfred with dispatch on load 44803. Please call us back at 713-555-0170." }] },
  { id: "c5", day: "today", at: "8:22 AM", loadRef: "HOU-44812", driverId: "d2", party: "driver", step: "Arrived at Gulf Coast Terminal", dur: 38, outcome: "Completed",
    captured: ["Gate-in 8:22", "Dock 14", "Detention clock started"],
    transcript: [{ who: "alfred", text: "Are you on the property at Gulf Coast Terminal?" }, { who: "driver", text: "Just checked in, dock fourteen." }, { who: "alfred", text: "Starting your detention clock. I'll notify Ridgeline if you pass two hours." }] },
  { id: "c7", day: "yesterday", at: "4:50 PM", loadRef: "HOU-44790", driverId: "d1", party: "driver", step: "Unloaded at Trinity Crossdock", dur: 58, outcome: "Completed",
    captured: ["POD signed by R. Salas 4:44 PM", "No exceptions", "Empty in Dallas"],
    transcript: [{ who: "alfred", text: "Are you unloaded at Trinity?" }, { who: "driver", text: "Signed at four forty-four. Guy's name was Salas." }, { who: "alfred", text: "Anything written on the POD — shortage, damage, refused pallets?" }, { who: "driver", text: "Nothing, clean." }] },
  { id: "c8", day: "yesterday", at: "2:15 PM", loadRef: "HOU-44790", driverId: null, party: "broker", step: "En route check call", dur: 47, outcome: "Voicemail",
    captured: ["Voicemail left with position and ETA"],
    transcript: [{ who: "alfred", text: "Voicemail: Sam, Alfred with a check call on 44790. Driver in Corsicana, ETA Dallas 4:30." }] },
];

const HOURLY = [4, 9, 17, 22, 19, 26, 31, 24, 18, 21, 14, 8];
const HOURLY_LABELS = ["6a", "7a", "8a", "9a", "10a", "11a", "12p", "1p", "2p", "3p", "4p", "5p"];

const STATUS = {
  needs_driver: { label: "Needs driver", muted: true },
  calling: { label: "On the line", fill: true, live: true },
  rolling: { label: "Rolling" },
  attention: { label: "Needs attention", fill: true },
  empty: { label: "Empty and available" },
  delivered: { label: "Delivered", muted: true },
  canceled: { label: "Canceled", muted: true },
  tonu: { label: "TONU", muted: true },
};

const LIMITS = [
  { t: "Negotiate or discuss rate", d: "Alfred never quotes, counters, or accepts a rate. Any rate conversation goes to the office." },
  { t: "Commit to an appointment", d: "Alfred reports appointment times and flags late risk. It doesn't agree to new ones — a person does." },
  { t: "Authorize money", d: "No advances, lumper payments, Comdata codes, or reimbursements. Alfred captures the amount and who's asking, then stops." },
  { t: "Accept or decline a load on the carrier's behalf", d: "Alfred can brief a driver on a load. It can't book, tender, or turn one down." },
  { t: "Speak for the carrier on an incident or claim", d: "Accident, injury, cargo damage, or a DOT stop ends the call plan and puts a person on the line. Alfred asks about injuries and safety, nothing else." },
  { t: "Give legal, medical, or hours-of-service advice", d: "Alfred reports what the ELD says. It never tells a driver they can keep driving." },
  { t: "Hide that it's an AI", d: "Every call opens with the disclosure, and Alfred repeats it any time it's asked." },
];

const TOGGLEABLE = [
  { id: "cell", label: "Share driver cell numbers with brokers", on: false, note: "Off by default. Brokers get the office number." },
  { id: "record", label: "Record calls", on: true, note: "Disclosure plays on every call. Two-party-consent states are always disclosed." },
  { id: "motion", label: "Never call a driver in motion", on: true, note: "Alfred waits for the ELD to show stopped, then calls." },
  { id: "quiet", label: "Respect per-driver quiet hours", on: true, note: "Uses the driver's home time zone, not the office's." },
  { id: "late", label: "Escalate late risk before the window closes", on: true, note: "Compares ETA against the appointment and hands off early rather than reporting a miss." },
];

const INTEGRATIONS = [
  { id: "mcleod", name: "McLeod LoadMaster", kind: "TMS", state: "connected", keyMask: "••••4f19", lastSync: "3 min ago", pulls: "Load numbers, stops, appointments, driver assignments", keyHint: "lm_live_…", where: "In McLeod: Utilities → API Access → key with dispatch read scope." },
  { id: "trimble", name: "Trimble TMW", kind: "TMS", state: "off", pulls: "Loads, stops, and driver assignments", keyHint: "tmw_…", where: "In TMW: Admin → Integrations → API keys." },
  { id: "rose", name: "Rose Rocket", kind: "TMS", state: "off", pulls: "Loads, stops, customers", keyHint: "rr_…", where: "In Rose Rocket: Settings → Developers → create a token." },
  { id: "motive", name: "Motive", kind: "ELD", state: "connected", keyMask: "••••b220", lastSync: "1 min ago", pulls: "Position, speed, duty status, hours remaining", keyHint: "mv_…", where: "In Motive: Admin → Integrations → API keys. Read scope on vehicles and HOS." },
  { id: "samsara", name: "Samsara", kind: "ELD", state: "connected", keyMask: "••••7ac1", lastSync: "4 min ago", pulls: "Position, speed, duty status, hours remaining", keyHint: "samsara_api_…", where: "In Samsara: Settings → Cloud API tokens." },
  { id: "macropoint", name: "MacroPoint", kind: "Visibility", state: "connected", keyMask: "••••90e3", lastSync: "12 min ago", pulls: "Pushes tracking to brokers who ask for it instead of calls", keyHint: "mp_…", where: "Your broker usually provisions this. Ask for a carrier API credential." },
  { id: "truckertools", name: "Trucker Tools", kind: "Visibility", state: "off", pulls: "Pushes tracking to brokers who ask for it instead of calls", keyHint: "tt_…", where: "Request a carrier key from Trucker Tools support." },
  { id: "hubspot", name: "HubSpot", kind: "CRM", state: "off", pulls: "Broker and shipper contacts, so Alfred knows who it's calling", keyHint: "pat-na1-…", where: "In HubSpot: Settings → Integrations → Private apps." },
  { id: "sheets", name: "Google Sheets", kind: "CRM", state: "off", pulls: "A shared sheet of loads, if that's what you run on today", keyHint: "Paste a share link", where: "Share the sheet with the service account shown after connecting." },
];

const RATECON_EXTRACT = {
  ref: "RL-99231", rate: "$2,275", commodity: "Palletized beverage, 41,800 lbs",
  puName: "Silver Eagle Distributors", puCity: "Houston, TX", puWindow: "Aug 9, 07:00–10:00",
  doName: "Alamo Distribution", doCity: "San Antonio, TX", doWindow: "Aug 9, 15:00–18:00",
  brokerName: "Ridgeline Logistics", brokerContact: "Dana Whitfield", brokerPhone: "(469) 555-0177",
};
const LOW_CONFIDENCE = ["puWindow", "commodity"];

/* -------------------------------- helpers ---------------------------------- */

const nowLabel = () => new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const totalAttempts = (a) => a.first + a.second + a.third;
const firstRate = (a) => a.first / totalAttempts(a);
const waitLabel = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
const staffById = (id) => STAFF.find((s) => s.id === id) || null;
const durLabel = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const firstPickup = (l) => l.stops.find((s) => s.type === "pickup") || l.stops[0];
const lastDrop = (l) => [...l.stops].reverse().find((s) => s.type === "drop") || l.stops[l.stops.length - 1];
const laneLabel = (l) => {
  const extra = l.stops.length - 2;
  return `${firstPickup(l).city.split(",")[0]} → ${lastDrop(l).city.split(",")[0]}${extra > 0 ? ` +${extra}` : ""}`;
};
const CLOSED = ["delivered", "canceled", "tonu"];

function parseClock(t) {
  const m = /(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(t || "");
  if (!m) return null;
  let h = parseInt(m[1], 10) % 12;
  if (/pm/i.test(m[3])) h += 12;
  return h * 60 + parseInt(m[2], 10);
}

function inQuietHours(at, driver) {
  const mins = parseClock(at);
  if (mins === null) return false;
  const local = (mins + driver.tzShift * 60 + 1440) % 1440;
  return local < 6 * 60 || local >= 21 * 60;
}

function localTimeFor(at, driver) {
  const mins = parseClock(at);
  if (mins === null || driver.tzShift === 0) return null;
  const local = (mins + driver.tzShift * 60 + 1440) % 1440;
  const h = Math.floor(local / 60), m = local % 60;
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"} ${driver.tz}`;
}

/** ETA past the appointment close, on any stop that isn't done yet */
function lateRisk(load) {
  for (const s of load.stops) {
    if (s.state === "complete" || s.state === "canceled") continue;
    const eta = parseClock(s.etaAt), end = parseClock(s.windowEnd);
    if (eta === null || end === null) continue;
    if (eta > end) return { stop: s, minutes: eta - end };
  }
  return null;
}

/** not enough drive time left to make the next stop */
function clockPressure(load, driver) {
  if (!load.hoursToNextStop || CLOSED.includes(load.status)) return null;
  if (driver.eld.driveLeft >= load.hoursToNextStop) return null;
  return { need: load.hoursToNextStop, have: driver.eld.driveLeft };
}

function routeContact(load) {
  const p = staffById(load.contact1), s = staffById(load.contact2);
  if (p && p.onHours) return { person: p, fallback: false };
  if (s) return { person: s, fallback: true, primary: p };
  return { person: p, fallback: false };
}

const openDetention = (l) => l.stops.find((s) => s.state === "arrived" && s.arrivedAt);

const scriptFor = (party, step, drv, load) =>
  party === "driver"
    ? [
        { who: "alfred", text: `Hi, this is Alfred calling for dispatch on load ${load.ref}. I'm an AI assistant and this call is recorded. Is this the driver on ${drv.truck}?` },
        { who: "driver", text: `Yeah, this is ${drv.name.split(" ")[0]}.` },
        { who: "alfred", text: step.detail },
        { who: "driver", text: "Go ahead." },
        ...(step.captures && step.captures.length
          ? [{ who: "alfred", text: `I need ${step.captures.join(", ").toLowerCase()} off the paperwork — take your time, I'll read it back.` },
             { who: "driver", text: "Give me a second… alright, go ahead." }]
          : [{ who: "alfred", text: "Where are you sitting, and what's your ETA?" }, { who: "driver", text: "North of Victoria on 59, about four hours out." }]),
        { who: "alfred", text: "Anything you need from the office — fuel, lumper, parking?" },
        { who: "driver", text: "No, running clean." },
        { who: "alfred", text: "Thank you. I'll update the office and the broker. Drive safe." },
      ]
    : [
        { who: "alfred", text: `Hi, this is Alfred, an AI assistant, calling on load ${load.ref} for ${load.broker.name}. This call is recorded.` },
        { who: "broker", text: "Go ahead." },
        { who: "alfred", text: step.detail },
        { who: "broker", text: "Got it, anything else?" },
        { who: "alfred", text: "That's everything. I'll call again on the next milestone." },
      ];

/* ------------------------------- primitives -------------------------------- */

function Micro({ children, style }) {
  return <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: T.ink3, ...style }}>{children}</div>;
}
function Tag({ children, solid }) {
  return <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.ink}`, background: solid ? T.ink : "transparent", color: solid ? "#fff" : T.ink, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
}
function Soft({ children }) {
  return <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.hair}`, color: T.ink2, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
}
function Chip({ status }) {
  const s = STATUS[status];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 500, padding: "3px 9px", borderRadius: 4, whiteSpace: "nowrap",
      background: s.fill ? T.ink : "transparent", color: s.fill ? "#fff" : s.muted ? T.ink3 : T.ink, border: `1px solid ${s.fill ? T.ink : s.muted ? T.hair : T.ink}` }}>
      {s.live && <span className="alf-dot" />}{s.label}
    </span>
  );
}
function Button({ children, onClick, kind = "primary", disabled, size = "md", title }) {
  const k = { primary: { background: T.ink, color: "#fff", border: `1px solid ${T.ink}` }, quiet: { background: "#fff", color: T.ink, border: `1px solid ${T.hair}` } }[kind];
  return (
    <button onClick={onClick} disabled={disabled} title={title} className="alf-btn" style={{
      ...k, font: "inherit", fontSize: size === "sm" ? 12.5 : 13.5, fontWeight: 500, letterSpacing: -0.15,
      padding: size === "sm" ? "6px 11px" : "8px 15px", borderRadius: 7, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1 }}>
      {children}
    </button>
  );
}
function Toggle({ on, onChange }) {
  return (
    <button onClick={() => onChange(!on)} aria-pressed={on} style={{ width: 42, height: 24, borderRadius: 999, padding: 2, cursor: "pointer", flexShrink: 0,
      background: on ? T.ink : "#fff", border: `1px solid ${on ? T.ink : T.hair}`, display: "flex", justifyContent: on ? "flex-end" : "flex-start", transition: "background .18s" }}>
      <span style={{ width: 18, height: 18, borderRadius: 999, background: on ? "#fff" : T.ink4, transition: "all .18s" }} />
    </button>
  );
}
function Disclosure({ title, meta, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <button onClick={() => setOpen((o) => !o)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", padding: "14px 20px", cursor: "pointer", textAlign: "left", font: "inherit" }}>
        <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{title}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {meta && <span style={{ fontSize: 12.5, color: T.ink3 }}>{meta}</span>}
          <span style={{ color: T.ink3, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
        </span>
      </button>
      {open && <div style={{ padding: "0 20px 18px" }}>{children}</div>}
    </div>
  );
}
function Field({ label, value, isMono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "6px 0" }}>
      <span style={{ fontSize: 13, color: T.ink3 }}>{label}</span>
      <span style={{ fontSize: 13, textAlign: "right", fontFamily: isMono ? mono : sans, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}
function Waveform({ active, invert }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2.5, height: 14 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={active ? "alf-bar" : ""} style={{ width: 2, borderRadius: 1, background: invert ? "#fff" : T.ink, height: active ? 14 : 4, animationDelay: `${i * 0.11}s`, opacity: active ? 1 : 0.3 }} />
      ))}
    </span>
  );
}
function Panel({ children }) {
  return <div style={{ background: T.surface, border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>{children}</div>;
}
function PanelHead({ title, note, right }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "16px 20px 14px", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.35 }}>{title}</div>
        {note && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 3, lineHeight: 1.45 }}>{note}</div>}
      </div>
      {right}
    </div>
  );
}
function Segmented({ value, onChange, options }) {
  return (
    <div style={{ display: "flex", border: `1px solid ${T.hair}`, borderRadius: 7, overflow: "hidden", flexWrap: "wrap" }}>
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} style={{ font: "inherit", fontSize: 12.5, fontWeight: 500, padding: "6px 11px", border: "none", cursor: "pointer",
          background: value === o.v ? T.ink : "#fff", color: value === o.v ? "#fff" : T.ink2 }}>{o.label}</button>
      ))}
    </div>
  );
}
const selectStyle = { font: "inherit", fontSize: 13, background: "#fff", color: T.ink, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "6px 10px", outline: "none", appearance: "none", cursor: "pointer" };

function Metric({ label, value, sub, emphasis }) {
  return (
    <div style={{ flex: "1 1 165px", padding: "18px 20px", borderRight: `1px solid ${T.hairSoft}` }}>
      <Micro>{label}</Micro>
      <div style={{ fontFamily: mono, fontVariantNumeric: "tabular-nums", fontSize: 32, letterSpacing: -1.5, marginTop: 8, lineHeight: 1, fontWeight: emphasis ? 600 : 400 }}>{value}</div>
      {sub && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 7 }}>{sub}</div>}
    </div>
  );
}
function AttemptBar({ a }) {
  const total = totalAttempts(a);
  return (
    <span style={{ display: "flex", width: "100%", height: 6, borderRadius: 3, overflow: "hidden", background: T.hairSoft }}>
      {[{ v: a.first, f: T.ink }, { v: a.second, f: "#9E9E9E" }, { v: a.third, f: "#DCDCDC" }].map((s, i) => <span key={i} style={{ width: `${(s.v / total) * 100}%`, background: s.f }} />)}
    </span>
  );
}
function Legend({ fill, label }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.ink3 }}>
    <span style={{ width: 9, height: 9, background: fill, borderRadius: 2, border: fill === "#DCDCDC" ? `1px solid ${T.hair}` : "none" }} />{label}</span>;
}
function EldLine({ driver }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: T.ink2 }}>
      {driver.eld.inMotion ? <Tag solid>in motion</Tag> : <Soft>stopped</Soft>}
      <span>{driver.eld.position}</span>
      <span style={{ fontFamily: mono, color: T.ink3 }}>{driver.eld.driveLeft}h drive left</span>
      <span style={{ fontSize: 11.5, color: T.ink4 }}>{driver.eld.provider}, {driver.eld.updated}m ago</span>
    </div>
  );
}

function DetentionClock({ stop, broker }) {
  const [, tick] = useState(0);
  useEffect(() => { const id = setInterval(() => tick((n) => n + 1), 30000); return () => clearInterval(id); }, []);
  const mins = Math.floor((Date.now() - stop.arrivedAt) / MIN);
  const over = mins >= 120;
  return (
    <div style={{ border: `1px solid ${over ? T.ink : T.hair}`, borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
        <Micro>Detention clock</Micro>
        {over && <Tag solid>billable</Tag>}
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 18, fontVariantNumeric: "tabular-nums" }}>{Math.floor(mins / 60)}h {String(mins % 60).padStart(2, "0")}m</span>
      </div>
      <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 7, lineHeight: 1.5 }}>
        On site at {stop.name} since gate-in. {over ? `Free time ended at 2h — ${mins - 120} billable minutes and counting. ${broker.name} was notified at the mark.` : `Free time ends at 2h. Alfred notifies ${broker.name} when it does.`}
      </div>
    </div>
  );
}

/* ------------------------------- paperwork --------------------------------- */

function Paperwork({ load }) {
  const got = load.paperwork.filter((d) => d.v).length;
  return (
    <div>
      {load.paperwork.length === 0 && <div style={{ fontSize: 13, color: T.ink3 }}>Nothing captured on this load.</div>}
      {load.paperwork.map((d, i) => (
        <div key={d.k} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}` }}>
          <span style={{ fontSize: 13.5, fontWeight: 500, minWidth: 110 }}>{d.k}</span>
          <span style={{ flex: 1, fontFamily: d.v ? mono : sans, fontSize: 13, color: d.v ? T.ink : T.ink3 }}>{d.v || "not captured yet"}</span>
          {d.v ? <span style={{ fontSize: 12, color: T.ink3 }}>{d.at}</span> : <Soft>on the next call</Soft>}
        </div>
      ))}
      {load.paperwork.length > 0 && (
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 11, lineHeight: 1.55, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 11 }}>
          {got} of {load.paperwork.length} captured by voice. Alfred reads every number back to the driver and files it only after they confirm. Physical copies still come in the way they do today — Alfred doesn't handle images.
        </div>
      )}
    </div>
  );
}

/* ------------------------------ recording ---------------------------------- */

function bars(seed, n = 44) {
  const out = []; let x = seed * 9301;
  for (let i = 0; i < n; i++) { x = (x * 9301 + 49297) % 233280; out.push(0.25 + (x / 233280) * 0.75); }
  return out;
}
function Recording({ seed, dur }) {
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const shape = useRef(bars(seed)).current;
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setPos((p) => (p >= dur ? (setPlaying(false), 0) : p + 1)), 90);
    return () => clearInterval(id);
  }, [playing, dur]);
  const pct = pos / dur;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: `1px solid ${T.hair}`, borderRadius: 8 }}>
      <button onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pause" : "Play"} style={{ width: 30, height: 30, borderRadius: 999, background: T.ink, color: "#fff", border: "none", cursor: "pointer", fontSize: 11, flexShrink: 0 }}>{playing ? "❚❚" : "▶"}</button>
      <span style={{ flex: 1, display: "flex", alignItems: "center", gap: 1.5, height: 26 }}>
        {shape.map((h, i) => <span key={i} style={{ flex: 1, height: `${h * 100}%`, borderRadius: 1, background: i / shape.length <= pct ? T.ink : "#D8D8D8" }} />)}
      </span>
      <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink2, minWidth: 74, textAlign: "right" }}>{durLabel(pos)} / {durLabel(dur)}</span>
    </div>
  );
}

/* -------------------------------- history ---------------------------------- */

/* one row of call history — used by the History tab and by a driver's profile */
function CallRow({ c, drivers, loads, open, onToggle, showDriver = true }) {
  const d = c.driverId ? drivers.find((x) => x.id === c.driverId) : null;
  const load = loads.find((l) => l.ref === c.loadRef);
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <button onClick={onToggle} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
        <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3, minWidth: 62 }}>{c.at}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{c.step}</span>
            <Soft>{c.party}</Soft>
            {c.outcome !== "Completed" && <Tag solid>{c.outcome}</Tag>}
          </span>
          <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>
            {c.loadRef}{showDriver ? ` · ${d ? `${d.name} · ${d.truck}` : load ? load.broker.contact : "Broker"}` : load ? ` · ${laneLabel(load)}` : ""}
          </span>
        </span>
        <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3 }}>{durLabel(c.dur)}</span>
        <span style={{ color: T.ink4, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
      </button>
      {open && (
        <div className="alf-fade" style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          <Recording seed={parseInt(c.id.slice(1), 10) + 3} dur={c.dur} />
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {c.captured.map((x, i) => <span key={i} style={{ fontSize: 12, border: `1px solid ${T.hair}`, borderRadius: 4, padding: "3px 8px", color: T.ink2 }}>{x}</span>)}
          </div>
          <div>
            <Micro style={{ marginBottom: 8 }}>Transcript</Micro>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {c.transcript.map((l, i) => (
                <div key={i} style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", color: l.who === "alfred" ? T.ink : T.ink4, minWidth: 42, paddingTop: 3 }}>{l.who}</span>
                  <span style={{ fontSize: 13.5, lineHeight: 1.45, color: l.who === "alfred" ? T.ink : T.ink2 }}>{l.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function History({ drivers, loads }) {
  const [day, setDay] = useState("today");
  const [driverId, setDriverId] = useState("all");
  const [loadRef, setLoadRef] = useState("all");
  const [openId, setOpenId] = useState(null);

  const rows = CALL_LOG.filter((c) => (day === "all" || c.day === day) && (driverId === "all" || c.driverId === driverId) && (loadRef === "all" || c.loadRef === loadRef));
  const refs = Array.from(new Set(CALL_LOG.map((c) => c.loadRef)));
  const talk = rows.reduce((s, c) => s + c.dur, 0);

  return (
    <Panel>
      <PanelHead title="Call history" note={`${rows.length} call${rows.length === 1 ? "" : "s"} · ${durLabel(talk)} of talk time. Every call is recorded and transcribed.`}
        right={<Segmented value={day} onChange={setDay} options={[{ v: "today", label: "Today" }, { v: "yesterday", label: "Yesterday" }, { v: "all", label: "All" }]} />} />
      <div style={{ display: "flex", gap: 8, padding: "0 20px 14px", flexWrap: "wrap" }}>
        <select value={driverId} onChange={(e) => setDriverId(e.target.value)} style={selectStyle}>
          <option value="all">All drivers</option>
          {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={loadRef} onChange={(e) => setLoadRef(e.target.value)} style={selectStyle}>
          <option value="all">All loads</option>
          {refs.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {(driverId !== "all" || loadRef !== "all") && (
          <button onClick={() => { setDriverId("all"); setLoadRef("all"); }} style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Clear</button>
        )}
      </div>
      {rows.length === 0 ? <div style={{ padding: "26px 20px 30px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13.5, color: T.ink3 }}>No calls match those filters.</div>
        : rows.map((c) => <CallRow key={c.id} c={c} drivers={drivers} loads={loads} open={openId === c.id} onToggle={() => setOpenId(openId === c.id ? null : c.id)} />)}
    </Panel>
  );
}

/* -------------------------------- overview --------------------------------- */

function LadderStrip({ waiting, load, jumped }) {
  const c1 = staffById(load.contact1), c2 = staffById(load.contact2);
  const names = [c1 ? c1.name : "Primary", c2 ? c2.name : "Second contact", "Hollis Grant", "Emergency contact"];
  const stage = jumped ? 2 : LADDER.reduce((acc, r, i) => (waiting >= r.at ? i : acc), 0);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 11, paddingTop: 11, borderTop: `1px dashed ${T.hair}`, flexWrap: "wrap" }}>
      <Micro style={{ marginRight: 2 }}>{jumped ? "Ladder skipped" : "Escalation"}</Micro>
      {LADDER.map((r, i) => (
        <React.Fragment key={r.at}>
          {i > 0 && <span style={{ width: 12, height: 1, background: T.hair }} />}
          <span style={{ fontSize: 11.5, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap", border: `1px solid ${i <= stage ? T.ink : T.hair}`,
            background: i === stage ? T.ink : "#fff", color: i === stage ? "#fff" : i < stage ? T.ink : T.ink3 }}>
            {names[i]}{r.at > 0 && !jumped ? ` +${r.at}m` : ""}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

function QueueItem({ q, drivers, loads, seat, onOpenLoad, onResolve }) {
  const d = drivers.find((x) => x.id === q.driverId);
  const load = loads.find((l) => l.ref === q.loadRef);
  const route = load ? routeContact(load) : null;
  const need = q.kind === "money" ? MONEY_KIND[q.money].to : NEEDS[q.kind];
  const me = staffById(seat);
  const allowed = me && me.can.includes(need);
  const urgent = q.kind === "incident" || q.waiting > 30;

  return (
    <div style={{ display: "flex", gap: 14, padding: "16px 20px", borderTop: `1px solid ${T.hairSoft}`, position: "relative" }}>
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: urgent ? T.ink : T.hair }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
          <Tag solid={q.kind === "incident"}>{KIND[q.kind]}</Tag>
          {q.kind === "money" && <Soft>{MONEY_KIND[q.money].label}</Soft>}
          {q.amount && <span style={{ fontFamily: mono, fontSize: 12.5 }}>{q.amount}</span>}
          <button onClick={() => onOpenLoad(q.loadRef)} style={{ font: "inherit", fontFamily: mono, fontSize: 12, color: T.ink2, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>{q.loadRef}</button>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>{d.name} · {d.truck}</span>
          <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, color: urgent ? T.ink : T.ink3 }}>waiting {waitLabel(q.waiting)}</span>
        </div>

        <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: -0.25, marginTop: 9 }}>{q.title}</div>

        <div style={{ borderLeft: `2px solid ${T.hair}`, paddingLeft: 12, marginTop: 10 }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{q.quote}</div>
          <Micro style={{ marginTop: 5 }}>Driver, on the call</Micro>
        </div>

        {q.intake && (
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: "12px 14px", marginTop: 12 }}>
            <Micro style={{ marginBottom: 8 }}>What Alfred got before it stopped</Micro>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 26px" }}>
              {q.intake.map(([k, v]) => (
                <span key={k} style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 150 }}>
                  <span style={{ fontSize: 11.5, color: T.ink3 }}>{k}</span>
                  <span style={{ fontSize: 13 }}>{v}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5, marginTop: 10 }}>{q.ask}</div>

        {load && <LadderStrip waiting={q.waiting} load={load} jumped={q.jumped} />}
        {route && route.person && !q.jumped && (
          <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 8 }}>
            Ringing now: {route.person.name}{route.fallback ? ` — ${route.primary ? route.primary.name.split(" ")[0] : "primary"} is off shift` : ""}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 13, alignItems: "center", flexWrap: "wrap" }}>
          <Button size="sm" onClick={() => onOpenLoad(q.loadRef)}>Take the call</Button>
          <Button size="sm" kind="quiet" disabled={!allowed} onClick={() => allowed && onResolve(q.id)}>Mark handled</Button>
          {!allowed && <span style={{ fontSize: 12, color: T.ink3 }}>Only {need} can clear this one.</span>}
        </div>
      </div>
    </div>
  );
}

function Overview({ queue, drivers, loads, seat, onOpenLoad, onResolve }) {
  const [filter, setFilter] = useState("all");
  const shown = filter === "all" ? queue : filter === "money" ? queue.filter((q) => q.kind === "money") : queue.filter((q) => q.kind === filter);
  const maxHour = Math.max(...HOURLY);
  const dueNow = loads.reduce((n, l) => n + l.plan.filter((s) => s.state === "due").length, 0);
  const active = loads.filter((l) => !CLOSED.includes(l.status));
  const late = active.map((l) => ({ l, r: lateRisk(l) })).filter((x) => x.r);
  const callsToday = HOURLY.reduce((a, b) => a + b, 0);
  const hoursSaved = (callsToday * 4.2) / 60;
  const dollarsSaved = Math.round(hoursSaved * 34);
  const incidents = queue.filter((q) => q.kind === "incident").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Panel>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <Metric label="Calls today" value={String(callsToday)} sub="Driver and broker, all loads" />
          <Metric label="On-time pickup" value="94%" sub="Last 30 days, 71 loads" />
          <Metric label="Scheduled calls due" value={String(dueNow)} sub={`Across ${active.length} active loads`} />
          <Metric label="Waiting on a person" value={String(queue.length)} sub={incidents ? `${incidents} is an incident` : "Alfred handed these off"} emphasis />
        </div>
        <div style={{ borderTop: `1px solid ${T.hairSoft}`, padding: "13px 20px", display: "flex", gap: 26, flexWrap: "wrap", alignItems: "baseline" }}>
          <Micro>What that's worth</Micro>
          <span style={{ fontSize: 13 }}><span style={{ fontFamily: mono, fontWeight: 600 }}>{hoursSaved.toFixed(1)}h</span> of dispatcher time back today</span>
          <span style={{ fontSize: 13 }}><span style={{ fontFamily: mono, fontWeight: 600 }}>${dollarsSaved}</span> at a $34 loaded hourly rate</span>
          <span style={{ fontSize: 13 }}><span style={{ fontFamily: mono, fontWeight: 600 }}>4.6h</span> detention billed this week</span>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>{queue.length} of {callsToday} calls needed a person — {Math.round((1 - queue.length / callsToday) * 100)}% handled end to end</span>
        </div>
      </Panel>

      {late.length > 0 && (
        <Panel>
          <PanelHead title="Late risk" note="ETA is past the appointment close. Alfred escalates before the window, not after the miss." />
          {late.map(({ l, r }) => (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap", position: "relative" }}>
              <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: T.ink }} />
              <span style={{ flex: 1, minWidth: 200 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{r.stop.name}</span>
                  <Tag solid>{waitLabel(r.minutes)} late</Tag>
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>
                  ETA {r.stop.etaAt} against a {r.stop.windowEnd} close · {l.broker.name}
                </span>
              </span>
              <Button size="sm" kind="quiet" onClick={() => onOpenLoad(l.ref)}>Open load</Button>
            </div>
          ))}
        </Panel>
      )}

      <Panel>
        <PanelHead title="Waiting on a person"
          note="Alfred stops on money, safety, appointments, and anything it can't authorize. Incidents skip the ladder and ring an ops manager immediately."
          right={<Segmented value={filter} onChange={setFilter} options={[
            { v: "all", label: `All ${queue.length}` }, { v: "incident", label: "Incidents" }, { v: "money", label: "Money" },
            { v: "appointment", label: "Appointments" }, { v: "concern", label: "Concerns" }, { v: "unreachable", label: "Callbacks" }]} />} />
        {shown.length === 0
          ? <div style={{ padding: "26px 20px 30px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13.5, color: T.ink3 }}>Nothing waiting.</div>
          : shown.map((q) => <QueueItem key={q.id} q={q} drivers={drivers} loads={loads} seat={seat} onOpenLoad={onOpenLoad} onResolve={onResolve} />)}
      </Panel>

      <Panel>
        <PanelHead title="Call volume" note="Today, 6am to 6pm. Driver reachability and wellbeing live on the Drivers tab." />
        <div style={{ padding: "18px 20px 18px" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 128 }}>
            {HOURLY.map((v, i) => <span key={i} style={{ flex: 1, height: `${(v / maxHour) * 100}%`, background: v === maxHour ? T.ink : "#D2D2D2", borderRadius: 2 }} />)}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            {HOURLY_LABELS.map((l, i) => <span key={i} style={{ flex: 1, textAlign: "center", fontFamily: mono, fontSize: 10, color: T.ink4 }}>{l}</span>)}
          </div>
          <div style={{ borderTop: `1px solid ${T.hairSoft}`, marginTop: 18, paddingTop: 16, display: "flex", flexWrap: "wrap", gap: 30 }}>
            {[["Busiest hour", "12p · 31 calls"], ["Voicemails left", "18"], ["Calls avoided by API push", "31"], ["Median handoff pickup", "9m"]].map(([k, v]) => (
              <span key={k} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <Micro>{k}</Micro>
                <span style={{ fontFamily: mono, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>{v}</span>
              </span>
            ))}
          </div>
        </div>
      </Panel>
    </div>
  );
}

/* ------------------------------- call panel -------------------------------- */

function CallPanel({ call, onEnd }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [call.lines.length]);
  const mm = String(Math.floor(call.elapsed / 60)).padStart(2, "0");
  const ss = String(call.elapsed % 60).padStart(2, "0");
  return (
    <div style={{ margin: "0 20px 16px", border: `1px solid ${T.ink}`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", background: T.ink, color: "#fff", flexWrap: "wrap" }}>
        <Waveform active={call.phase === "connected"} invert />
        <span style={{ fontSize: 13, fontWeight: 500 }}>{call.phase === "dialing" ? `Dialing ${call.partyName}…` : `On the line with ${call.partyName}`}</span>
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, opacity: 0.75 }}>{mm}:{ss}</span>
        <button onClick={onEnd} style={{ font: "inherit", fontSize: 12, color: "#fff", background: "none", border: "1px solid rgba(255,255,255,.4)", borderRadius: 5, padding: "2px 8px", cursor: "pointer" }}>End</button>
      </div>
      {call.lines.length > 0 && (
        <div ref={ref} style={{ maxHeight: 180, overflowY: "auto", padding: 13, display: "flex", flexDirection: "column", gap: 9 }}>
          {call.lines.map((l, i) => (
            <div key={i} className="alf-fade" style={{ display: "flex", gap: 10 }}>
              <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", color: l.who === "alfred" ? T.ink : T.ink4, minWidth: 42, paddingTop: 3 }}>{l.who}</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: l.who === "alfred" ? T.ink : T.ink2 }}>{l.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- call plan -------------------------------- */

function StepMarker({ state }) {
  if (state === "done") return <span style={{ width: 15, height: 15, borderRadius: 999, background: T.ink, color: "#fff", fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</span>;
  if (state === "due") return <span style={{ width: 15, height: 15, borderRadius: 999, border: `2px solid ${T.ink}`, background: "#fff", flexShrink: 0, boxSizing: "border-box" }} />;
  return <span style={{ width: 15, height: 15, borderRadius: 999, border: `1px solid ${T.hair}`, background: "#fff", flexShrink: 0, boxSizing: "border-box" }} />;
}

function CallPlan({ load, driver, onCallStep, onPatchStep, onAddStep, busy }) {
  const due = load.plan.find((s) => s.state === "due");
  const [openId, setOpenId] = useState(due ? due.id : null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: "", at: "" });
  const done = load.plan.filter((s) => s.state === "done").length;
  const auto = load.broker.updateVia !== "call";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <Micro>{done} of {load.plan.length} steps done · {load.stops.length} stops</Micro>
        <button onClick={() => setAdding((a) => !a)} style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>{adding ? "Cancel" : "Add a call"}</button>
      </div>

      {adding && (
        <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: 12, marginBottom: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <input placeholder="What should Alfred say? e.g. broker wants the load bars checked" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            style={{ font: "inherit", fontSize: 13.5, border: `1px solid ${T.hair}`, borderRadius: 6, padding: "8px 10px", outline: "none" }} />
          <div style={{ display: "flex", gap: 8 }}>
            <input placeholder="Time" value={draft.at} onChange={(e) => setDraft({ ...draft, at: e.target.value })}
              style={{ font: "inherit", fontFamily: mono, fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 6, padding: "8px 10px", outline: "none", width: 130 }} />
            <Button size="sm" disabled={!draft.title.trim()} onClick={() => { onAddStep(load.id, draft); setDraft({ title: "", at: "" }); setAdding(false); }}>Add to plan</Button>
          </div>
        </div>
      )}

      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 7, top: 8, bottom: 8, width: 1, background: T.hairSoft }} />
        {load.plan.map((s) => {
          const open = openId === s.id;
          const pushed = s.party === "broker" && auto;
          const quiet = s.party === "driver" && inQuietHours(s.at, driver);
          const blocked = s.party === "driver" && driver.eld.inMotion;
          const local = s.party === "driver" ? localTimeFor(s.at, driver) : null;
          const stop = s.stopSeq ? load.stops.find((x) => x.seq === s.stopSeq) : null;
          return (
            <div key={s.id} style={{ position: "relative", paddingLeft: 26, paddingBottom: 4 }}>
              <span style={{ position: "absolute", left: 0, top: 10 }}><StepMarker state={s.state} /></span>
              <button onClick={() => setOpenId(open ? null : s.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", padding: "8px 6px 8px 0", cursor: "pointer", textAlign: "left", font: "inherit", borderRadius: 6 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13.5, fontWeight: s.state === "due" ? 600 : 500, color: s.state === "scheduled" ? T.ink2 : T.ink, letterSpacing: -0.2 }}>{s.title}</span>
                    <Soft>{s.party}</Soft>
                    {s.extra && <Tag>extra call</Tag>}
                    {pushed && <Soft>{UPDATE_VIA[load.broker.updateVia]}</Soft>}
                    {quiet && s.state !== "done" && <Tag>quiet hours</Tag>}
                  </span>
                </span>
                <span style={{ fontFamily: mono, fontSize: 11.5, color: s.state === "due" ? T.ink : T.ink3 }}>{s.at || "—"}</span>
              </button>

              {open && (
                <div className="alf-fade" style={{ padding: "4px 0 14px" }}>
                  <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>{s.detail}</div>
                  {stop && <div style={{ fontSize: 12, color: T.ink3, marginTop: 6 }}>{stop.city} · appointment {stop.window}</div>}
                  {s.captures.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                      {s.captures.map((c) => <span key={c} style={{ fontSize: 11.5, border: `1px solid ${T.hair}`, borderRadius: 4, padding: "2px 7px", color: T.ink2 }}>{c}</span>)}
                    </div>
                  )}
                  {local && <div style={{ fontSize: 12, color: T.ink3, marginTop: 6 }}>{s.at} office time is {local} for {driver.name.split(" ")[0]}.</div>}
                  {pushed && <div style={{ fontSize: 12, color: T.ink3, marginTop: 6 }}>{load.broker.name} takes updates through {UPDATE_VIA[load.broker.updateVia]}. Alfred pushes this instead of calling {load.broker.contact}.</div>}

                  <div style={{ marginTop: 10 }}>
                    <Micro style={{ marginBottom: 5 }}>Notes for this call</Micro>
                    <textarea value={s.notes} placeholder="Anything Alfred should say, ask, or pass along." onChange={(e) => onPatchStep(load.id, s.id, { notes: e.target.value })} rows={2}
                      style={{ width: "100%", font: "inherit", fontSize: 13, color: T.ink, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 7, padding: "8px 10px", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.45 }} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <input value={s.at} placeholder="Time" onChange={(e) => onPatchStep(load.id, s.id, { at: e.target.value })}
                      style={{ font: "inherit", fontFamily: mono, fontSize: 12.5, border: `1px solid ${T.hair}`, borderRadius: 6, padding: "6px 9px", outline: "none", width: 110 }} />
                    {s.state !== "done" && (
                      <>
                        <Button size="sm" disabled={busy || (blocked && !pushed)} onClick={() => onCallStep(load.id, s.id)} title={blocked && !pushed ? "Driver is in motion" : undefined}>
                          {pushed ? "Push update" : "Call now"}
                        </Button>
                        <Button size="sm" kind="quiet" onClick={() => onPatchStep(load.id, s.id, { state: s.state === "due" ? "scheduled" : "due" })}>{s.state === "due" ? "Hold" : "Mark due"}</Button>
                      </>
                    )}
                    {s.state === "done" && <span style={{ fontSize: 12.5, color: T.ink3 }}>Completed at {s.at}</span>}
                  </div>

                  {blocked && !pushed && s.state !== "done" && (
                    <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 9, lineHeight: 1.5 }}>
                      {driver.name.split(" ")[0]} is driving at {driver.eld.speed} mph. Alfred holds this call and places it when the ELD shows stopped.
                    </div>
                  )}
                  {quiet && s.state !== "done" && !blocked && (
                    <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 9, lineHeight: 1.5 }}>
                      That lands inside {driver.name.split(" ")[0]}'s quiet hours ({driver.quiet} {driver.tz}). Alfred waits for the window to open unless you override it.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------- inspector -------------------------------- */

function Inspector({ load, driver, drivers, call, onCallStep, onEndCall, onPatchStep, onAddStep, onSetBrokerPref, onRelay, onClose }) {
  const [relayOpen, setRelayOpen] = useState(false);
  const [relayTo, setRelayTo] = useState("");

  if (!load) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 10, padding: 32, textAlign: "center" }}>
        <Waveform active={false} />
        <div style={{ fontSize: 14, color: T.ink2, marginTop: 4 }}>Select a load</div>
        <div style={{ fontSize: 12.5, color: T.ink3, maxWidth: 210, lineHeight: 1.5 }}>The call plan, stops, paperwork, and contacts open here.</div>
      </div>
    );
  }
  const activeCall = call && call.loadId === load.id ? call : null;
  const c1 = staffById(load.contact1), c2 = staffById(load.contact2);
  const route = routeContact(load);
  const nextDue = load.plan.find((s) => s.state === "due");
  const det = openDetention(load);
  const late = lateRisk(load);
  const pressure = clockPressure(load, driver);
  const pushed = nextDue && nextDue.party === "broker" && load.broker.updateVia !== "call";
  const blocked = nextDue && nextDue.party === "driver" && driver.eld.inMotion;

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div style={{ padding: "18px 20px 15px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: 12, color: T.ink3, letterSpacing: 0.4 }}>{load.ref}</span>
          <button onClick={onClose} className="alf-close" style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: "pointer" }}>Close</button>
        </div>
        <div style={{ fontSize: 21, fontWeight: 600, letterSpacing: -0.6, marginTop: 6, lineHeight: 1.25 }}>{laneLabel(load)}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 10, flexWrap: "wrap" }}>
          <Chip status={activeCall ? "calling" : load.status} />
          {load.rate && <span style={{ fontFamily: mono, fontSize: 12.5 }}>{load.rate}</span>}
          <span style={{ fontSize: 11.5, color: T.ink3 }}>{driver.name} · {driver.truck}</span>
        </div>
        <div style={{ marginTop: 10 }}><EldLine driver={driver} /></div>

        {late && (
          <div style={{ marginTop: 14, padding: "11px 13px", border: `1px solid ${T.ink}`, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Tag solid>late risk</Tag>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>{waitLabel(late.minutes)} past the {late.stop.name} window</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>
              ETA {late.stop.etaAt} against a {late.stop.windowEnd} close. Alfred won't agree to a new appointment — it hands this to {route.person ? route.person.name : "the load contact"} while there's still time to move it.
            </div>
          </div>
        )}

        {pressure && (
          <div style={{ marginTop: 10, padding: "11px 13px", border: `1px solid ${T.hair}`, borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Soft>clock pressure</Soft>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{pressure.have}h drive left against {pressure.need}h to the next stop</span>
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 5, lineHeight: 1.5 }}>
              Alfred reports the hours. It never tells a driver whether they can run it — that decision is the office's.
            </div>
          </div>
        )}

        {!activeCall && nextDue && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, padding: "11px 13px", border: `1px solid ${T.ink}`, borderRadius: 8, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <Micro>Next step</Micro>
              <div style={{ fontSize: 13.5, fontWeight: 500, marginTop: 3 }}>{nextDue.title}</div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 1 }}>
                {blocked && !pushed ? `Holding — ${driver.name.split(" ")[0]} is in motion`
                  : pushed ? `${UPDATE_VIA[load.broker.updateVia]} · no call needed`
                  : `${nextDue.party === "driver" ? driver.name : load.broker.contact} · ${nextDue.at || "unscheduled"}`}
              </div>
            </div>
            <Button size="sm" disabled={blocked && !pushed} onClick={() => onCallStep(load.id, nextDue.id)}>{pushed ? "Push update" : "Call now"}</Button>
          </div>
        )}
      </div>

      {activeCall && <CallPanel call={activeCall} onEnd={onEndCall} />}
      {det && <div style={{ padding: "0 20px 16px" }}><DetentionClock stop={det} broker={load.broker} /></div>}

      <Disclosure title="Call plan" meta={`${load.plan.filter((s) => s.state === "done").length}/${load.plan.length}`} defaultOpen>
        <CallPlan load={load} driver={driver} onCallStep={onCallStep} onPatchStep={onPatchStep} onAddStep={onAddStep} busy={!!call} />
      </Disclosure>

      <Disclosure title="Stops" meta={`${load.stops.length} stops`}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 5, top: 10, bottom: 10, width: 1, background: T.hairSoft }} />
          {load.stops.map((s) => (
            <div key={s.seq} style={{ position: "relative", paddingLeft: 22, paddingBottom: 14 }}>
              <span style={{ position: "absolute", left: 0, top: 5, width: 11, height: 11, borderRadius: s.type === "pickup" ? 2 : 999, boxSizing: "border-box",
                background: s.state === "complete" ? T.ink : "#fff", border: `${s.state === "arrived" ? 2 : 1}px solid ${s.state === "canceled" ? T.hair : T.ink}` }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{s.name}</span>
                <Soft>{s.type}</Soft>
                {s.state === "arrived" && <Tag solid>on site</Tag>}
                {late && late.stop.seq === s.seq && <Tag>late risk</Tag>}
              </div>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
                {s.city} · {s.window}{s.etaAt && s.state !== "complete" ? ` · ETA ${s.etaAt}` : ""}
              </div>
            </div>
          ))}
        </div>
      </Disclosure>

      <Disclosure title="Paperwork" meta={`${load.paperwork.filter((d) => d.v).length}/${load.paperwork.length}`}>
        <Paperwork load={load} />
      </Disclosure>

      <Disclosure title="Company contacts" meta={route.person ? route.person.name : "Not set"}>
        {c1 && (<div><Micro>Primary</Micro><Field label={c1.name} value={c1.role} /><Field label="Phone" value={c1.phone} isMono /><Field label="Available" value={c1.hours} /></div>)}
        {c2 && (<div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.hairSoft}` }}><Micro>Second contact</Micro><Field label={c2.name} value={c2.role} /><Field label="Phone" value={c2.phone} isMono /><Field label="Available" value={c2.hours} /></div>)}
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>
          {route.person ? (route.fallback ? `Right now Alfred would reach ${route.person.name} — ${c1 ? c1.name.split(" ")[0] : "the primary"} is off shift.` : `Right now Alfred would reach ${route.person.name}.`) : "No one is set to take a handoff on this load."}
        </div>
      </Disclosure>

      <Disclosure title="Driver" meta={driver.name}>
        <Field label="Phone" value={driver.phone} isMono />
        <Field label="Truck / trailer" value={`${driver.truck} · ${driver.trailer}`} />
        <Field label="Language" value={driver.language} />
        <Field label="Time zone" value={`${driver.tz} · quiet ${driver.quiet}`} />
        <Field label="First-call connect" value={`${Math.round(firstRate(driver.attempts) * 100)}% of ${totalAttempts(driver.attempts)}`} isMono />
        <Field label="ELD" value={`${driver.eld.provider} · ${driver.eld.dutyLeft}h duty left`} />
        <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.hairSoft}` }}>{driver.note}</div>
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.hairSoft}` }}>
          {!relayOpen ? (
            <button onClick={() => setRelayOpen(true)} style={{ font: "inherit", fontSize: 13, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}>Hand the rest of this load to another driver</button>
          ) : (
            <div className="alf-fade">
              <Micro style={{ marginBottom: 6 }}>Relay to</Micro>
              <select value={relayTo} onChange={(e) => setRelayTo(e.target.value)} style={{ ...selectStyle, width: "100%", boxSizing: "border-box" }}>
                <option value="">Pick a driver</option>
                {drivers.filter((d) => d.id !== driver.id).map((d) => <option key={d.id} value={d.id}>{d.name} — {d.truck} · {d.eld.driveLeft}h left</option>)}
              </select>
              <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 8 }}>
                Remaining steps move to the new driver, who gets a fresh assignment call. Completed steps and paperwork stay on the load.
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <Button size="sm" disabled={!relayTo} onClick={() => { onRelay(load.id, relayTo); setRelayOpen(false); setRelayTo(""); }}>Hand it over</Button>
                <Button size="sm" kind="quiet" onClick={() => setRelayOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </Disclosure>

      <Disclosure title="Broker" meta={load.broker.name}>
        <Field label="Contact" value={load.broker.contact} />
        <Field label="Phone" value={load.broker.phone} isMono />
        <div style={{ marginTop: 13, paddingTop: 13, borderTop: `1px solid ${T.hairSoft}` }}>
          <Micro style={{ marginBottom: 6 }}>How they want updates</Micro>
          <select value={load.broker.updateVia} onChange={(e) => onSetBrokerPref(load.id, e.target.value)} style={{ ...selectStyle, width: "100%", boxSizing: "border-box" }}>
            {Object.entries(UPDATE_VIA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 8 }}>
            {load.broker.updateVia === "call"
              ? `Alfred calls ${load.broker.contact} at each milestone (${load.broker.cadence.toLowerCase()}).`
              : load.broker.updateVia === "portal"
              ? "Portal check calls can't be automated yet. Broker steps escalate to the office instead of dialing."
              : `Milestones push to ${UPDATE_VIA[load.broker.updateVia]}. Alfred calls only if the push fails or something needs a human answer.`}
          </div>
        </div>
      </Disclosure>

      <Disclosure title="Activity" meta={load.activity.length ? String(load.activity.length) : "None"}>
        {load.activity.length === 0 ? <div style={{ fontSize: 13, color: T.ink3 }}>Nothing yet.</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {[...load.activity].reverse().map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 12 }}>
                <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 56, paddingTop: 2 }}>{a.t}</span>
                <div><div style={{ fontSize: 13, fontWeight: 500 }}>{a.head}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 2 }}>{a.body}</div></div>
              </div>
            ))}
          </div>
        )}
      </Disclosure>
      <div style={{ height: 20 }} />
    </div>
  );
}

/* --------------------------------- settings -------------------------------- */

function IntegrationRow({ item, onConnect, onDisconnect }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [testing, setTesting] = useState(false);
  const connected = item.state === "connected";
  return (
    <div style={{ borderTop: `1px solid ${T.hairSoft}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 170 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{item.name}</span>
            <Soft>{item.kind}</Soft>
            {connected && <Tag solid>connected</Tag>}
          </span>
          <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>
            {connected ? `${item.pulls} · last sync ${item.lastSync}` : item.pulls}
          </span>
        </span>
        {connected ? (
          <>
            <span style={{ fontFamily: mono, fontSize: 12, color: T.ink2 }}>{item.keyMask}</span>
            <Button size="sm" kind="quiet" onClick={() => onDisconnect(item.id)}>Disconnect</Button>
          </>
        ) : <Button size="sm" kind="quiet" onClick={() => setOpen((o) => !o)}>{open ? "Cancel" : "Connect"}</Button>}
      </div>
      {open && !connected && (
        <div className="alf-fade" style={{ padding: "0 20px 18px" }}>
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Micro style={{ marginBottom: 5 }}>API key</Micro>
              <input value={key} onChange={(e) => setKey(e.target.value)} placeholder={item.keyHint}
                style={{ width: "100%", font: "inherit", fontFamily: mono, fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "9px 11px", outline: "none", boxSizing: "border-box" }} />
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 6, lineHeight: 1.45 }}>{item.where}</div>
            </div>
            <div>
              <Micro style={{ marginBottom: 6 }}>What Alfred may do</Micro>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Read only", "Read and write back"].map((m) => (
                  <span key={m} style={{ fontSize: 12.5, padding: "5px 10px", borderRadius: 6, border: `1px solid ${m === "Read only" ? T.ink : T.hair}`, background: m === "Read only" ? T.ink : "#fff", color: m === "Read only" ? "#fff" : T.ink2 }}>{m}</span>
                ))}
              </div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>
                Start read-only. Write-back posts call outcomes, ETAs, POD details, and empty status into {item.name} — turn it on once you trust what Alfred is capturing.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Button size="sm" disabled={!key.trim() || testing} onClick={() => { setTesting(true); setTimeout(() => { setTesting(false); onConnect(item.id, key); setOpen(false); setKey(""); }, 1000); }}>
                {testing ? "Testing…" : "Test and connect"}
              </Button>
              {testing && <Waveform active />}
              {!key.trim() && <span style={{ fontSize: 12, color: T.ink3 }}>Any value works in this prototype.</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Settings() {
  const [tab, setTab] = useState("integrations");
  const [flags, setFlags] = useState(TOGGLEABLE);
  const [items, setItems] = useState(INTEGRATIONS);
  const connect = (id, key) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, state: "connected", lastSync: "just now", keyMask: `••••${(key || "").slice(-4) || "8f2c"}` } : x)));
  const disconnect = (id) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, state: "off", lastSync: null } : x)));
  const groups = [["TMS", "Dispatch software"], ["ELD", "Hours and location"], ["Visibility", "Broker tracking"], ["CRM", "Sales and customers"]];
  const connected = items.filter((i) => i.state === "connected").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22, maxWidth: 760 }}>
      <Segmented value={tab} onChange={setTab} options={[{ v: "integrations", label: "Integrations" }, { v: "calling", label: "Calling" }, { v: "limits", label: "Limits" }]} />

      {tab === "integrations" && (
        <>
          <Panel>
            <PanelHead title="Connected systems" note={`${connected} connected. With a TMS, load numbers resolve on their own. Without one, everything still works from the ratecon.`} />
            {groups.map(([kind, label]) => {
              const rows = items.filter((i) => i.kind === kind);
              if (!rows.length) return null;
              return (
                <div key={kind}>
                  <div style={{ padding: "12px 20px 6px", borderTop: `1px solid ${T.hairSoft}` }}><Micro>{label}</Micro></div>
                  {rows.map((i) => <IntegrationRow key={i.id} item={i} onConnect={connect} onDisconnect={disconnect} />)}
                </div>
              );
            })}
            <div style={{ padding: "14px 20px 18px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, color: T.ink2, lineHeight: 1.55 }}>
              A stale ELD key is worse than no key — Alfred's don't-call-in-motion rule depends on it. If a sync goes quiet, that shows up on Overview, not just here.
            </div>
          </Panel>
          <Panel>
            <PanelHead title="If nothing is connected" note="What Alfred falls back to, in order." />
            {[["Rate confirmation upload", "Full load and a stop-aware call plan built from the document. This is the default and it's fine."],
              ["Load number lookup", "Needs a TMS. Without one this path is hidden."],
              ["Manual entry", "Always available. Load number, two cities, and a contact is the minimum."]].map(([t, d], i) => (
              <div key={t} style={{ display: "flex", gap: 12, padding: "13px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
                <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 18, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
                <div><div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{t}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 3 }}>{d}</div></div>
              </div>
            ))}
          </Panel>
        </>
      )}

      {tab === "calling" && (
        <Panel>
          <PanelHead title="How Alfred calls" note="Voice only for now. Alfred doesn't text, and it doesn't handle images — paperwork details are read back and confirmed on the call." />
          {flags.map((f) => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{f.label}</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{f.note}</div>
              </div>
              <Toggle on={f.on} onChange={(v) => setFlags((xs) => xs.map((x) => (x.id === f.id ? { ...x, on: v } : x)))} />
            </div>
          ))}
          <div style={{ padding: "13px 20px 16px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12, color: T.ink3, lineHeight: 1.55 }}>
            The recording disclosure plays on every call regardless of these settings. Drivers acknowledge it at onboarding and can withdraw consent by telling Alfred, which routes their calls to a person.
          </div>
        </Panel>
      )}

      {tab === "limits" && (
        <Panel>
          <PanelHead title="What Alfred will never do" note="Fixed. Not switchable from this screen, by a driver on a call, or by a broker asking nicely. Hand this list to a safety director or an insurer." />
          {LIMITS.map((l, i) => (
            <div key={l.t} style={{ padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}`, display: "flex", gap: 12 }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 18, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
              <div><div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{l.t}</div><div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 3 }}>{l.d}</div></div>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}

/* --------------------------------- intake ---------------------------------- */

const EMPTY_FORM = {
  ref: "", rate: "", commodity: "", puName: "", puCity: "", puWindow: "",
  doName: "", doCity: "", doWindow: "", brokerName: "", brokerContact: "", brokerPhone: "",
  updateVia: "call", driverId: "d1", contact1: "s1", contact2: "", secondOpen: false,
};

function ChoiceRow({ title, note, tag, onClick }) {
  return (
    <button onClick={onClick} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, textAlign: "left", font: "inherit", padding: "16px 18px", background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 9, cursor: "pointer" }}>
      <span style={{ flex: 1 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 500, letterSpacing: -0.25 }}>{title}</span>
          {tag && <Tag solid>{tag}</Tag>}
        </span>
        <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3, lineHeight: 1.45 }}>{note}</span>
      </span>
      <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
    </button>
  );
}

function VField({ label, value, onChange, placeholder, source, flag, isMono, half }) {
  return (
    <div style={{ flex: half ? "1 1 46%" : "1 1 100%", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
        <Micro>{label}</Micro>
        {source && <span style={{ fontFamily: mono, fontSize: 9.5, color: T.ink4 }}>{source}</span>}
        {flag && <Tag>check</Tag>}
      </div>
      <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", font: "inherit", fontSize: 14, color: T.ink, background: "#fff", border: `1px solid ${flag ? T.ink : T.hair}`, borderRadius: 7, padding: "9px 11px", outline: "none", boxSizing: "border-box", fontFamily: isMono ? mono : sans }} />
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <div>
      <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: -0.25, marginBottom: note ? 2 : 10 }}>{title}</div>
      {note && <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 10, lineHeight: 1.45 }}>{note}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>{children}</div>
    </div>
  );
}

function ContactPicker({ value, onChange, label, allowNone }) {
  const s = staffById(value);
  return (
    <div style={{ flex: "1 1 100%" }}>
      <Micro style={{ marginBottom: 5 }}>{label}</Micro>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...selectStyle, width: "100%", fontSize: 14, padding: "9px 11px", boxSizing: "border-box" }}>
        {allowNone && <option value="">None</option>}
        {STAFF.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.role}</option>)}
      </select>
      {s && (
        <div style={{ display: "flex", gap: 14, marginTop: 7, flexWrap: "wrap" }}>
          <span style={{ fontFamily: mono, fontSize: 12, color: T.ink2 }}>{s.phone}</span>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>Available {s.hours}</span>
          {!s.onHours && <Soft>off shift now</Soft>}
        </div>
      )}
    </div>
  );
}

function NewLoadSheet({ onClose, onCreate, drivers }) {
  const [step, setStep] = useState("choose");
  const [lookupRef, setLookupRef] = useState("");
  const [fileName, setFileName] = useState("");
  const [readLine, setReadLine] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [extracted, setExtracted] = useState({});
  const fileRef = useRef(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const READ = ["Reading the document", "Pulling stops and appointments", "Matching the broker", "Building a stop-aware call plan"];

  useEffect(() => {
    if (step !== "reading") return;
    if (readLine < READ.length) { const t = setTimeout(() => setReadLine((n) => n + 1), 500); return () => clearTimeout(t); }
    const t = setTimeout(() => {
      setForm((f) => ({ ...f, ...RATECON_EXTRACT }));
      setExtracted({ ref: "p.1", rate: "p.1", commodity: "p.1", puName: "p.1", puCity: "p.1", puWindow: "p.1", doName: "p.2", doCity: "p.2", doWindow: "p.2", brokerName: "p.1", brokerContact: "p.1", brokerPhone: "p.1" });
      setStep("verify");
    }, 450);
    return () => clearTimeout(t);
  }, [step, readLine]);

  const flagged = Object.keys(extracted).filter((k) => LOW_CONFIDENCE.includes(k));
  const canSave = form.ref.trim() && form.puCity.trim() && form.doCity.trim() && form.contact1;

  return (
    <div className="alf-fade" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "5vh 16px 16px", zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} className="alf-rise" style={{ width: "100%", maxWidth: 560, background: "#fff", borderRadius: 12, border: `1px solid ${T.ink}`, maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 22px 14px", borderBottom: step === "choose" ? "none" : `1px solid ${T.hairSoft}` }}>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: -0.4 }}>{step === "verify" ? "Check the details" : "New load"}</div>
          <div style={{ fontSize: 13, color: T.ink3, marginTop: 3, lineHeight: 1.45 }}>
            {step === "choose" && "Start with whatever you already have."}
            {step === "lookup" && "Alfred will pull the load from your system."}
            {step === "reading" && (fileName ? `Reading ${fileName}` : `Looking up ${lookupRef}`)}
            {step === "verify" && (Object.keys(extracted).length
              ? `Filled in from the rate confirmation. ${flagged.length ? `${flagged.length} field${flagged.length > 1 ? "s" : ""} worth a second look.` : "Everything came through clean."}`
              : "Enter what you have. Load number, both cities, and a contact are required.")}
          </div>
        </div>

        <div style={{ padding: "18px 22px 20px", overflowY: "auto" }}>
          {step === "choose" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <ChoiceRow title="Upload the rate confirmation" tag="Fastest" note="Alfred fills in the load, stops, broker and rate, then builds the call plan around the stops." onClick={() => fileRef.current && fileRef.current.click()} />
              <ChoiceRow title="Look up a load number" note="Pulls from your TMS or load board." onClick={() => setStep("lookup")} />
              <ChoiceRow title="Enter it by hand" note="No system to pull from. Type in what you have." onClick={() => setStep("verify")} />
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => { const f = e.target.files && e.target.files[0]; setFileName(f ? f.name : "ratecon.pdf"); setReadLine(0); setStep("reading"); }} style={{ display: "none" }} />
            </div>
          )}

          {step === "lookup" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <VField label="Load number" value={lookupRef} onChange={(v) => setLookupRef(v.toUpperCase())} placeholder="e.g. HOU-44821" isMono />
              <div style={{ display: "flex", gap: 8 }}>
                <Button disabled={!lookupRef.trim()} onClick={() => { setReadLine(0); setFileName(""); setStep("reading"); }}>Find load</Button>
                <Button kind="quiet" onClick={() => setStep("choose")}>Back</Button>
              </div>
            </div>
          )}

          {step === "reading" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 11, padding: "6px 0 10px" }}>
              {READ.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, opacity: i <= readLine ? 1 : 0.25 }}>
                  {i < readLine ? <span style={{ fontSize: 13, width: 16 }}>✓</span> : i === readLine ? <span style={{ width: 16 }}><Waveform active /></span> : <span style={{ width: 16, textAlign: "center", color: T.ink4 }}>·</span>}
                  <span style={{ fontSize: 13.5 }}>{s}</span>
                </div>
              ))}
            </div>
          )}

          {step === "verify" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <Section title="Load">
                <VField half label="Load number" value={form.ref} onChange={set("ref")} placeholder="Required" source={extracted.ref} isMono />
                <VField half label="Rate" value={form.rate} onChange={set("rate")} placeholder="$" source={extracted.rate} isMono />
                <VField label="Commodity and weight" value={form.commodity} onChange={set("commodity")} placeholder="Optional" source={extracted.commodity} flag={LOW_CONFIDENCE.includes("commodity") && extracted.commodity} />
              </Section>
              <Section title="Pickup" note="Multi-pick loads: save this one, then add stops from the load panel — the plan rebuilds around them.">
                <VField label="Shipper" value={form.puName} onChange={set("puName")} source={extracted.puName} />
                <VField half label="City, state" value={form.puCity} onChange={set("puCity")} placeholder="Required" source={extracted.puCity} />
                <VField half label="Window" value={form.puWindow} onChange={set("puWindow")} source={extracted.puWindow} flag={LOW_CONFIDENCE.includes("puWindow") && extracted.puWindow} />
              </Section>
              <Section title="Drop-off">
                <VField label="Consignee" value={form.doName} onChange={set("doName")} source={extracted.doName} />
                <VField half label="City, state" value={form.doCity} onChange={set("doCity")} placeholder="Required" source={extracted.doCity} />
                <VField half label="Window" value={form.doWindow} onChange={set("doWindow")} source={extracted.doWindow} />
              </Section>
              <Section title="Broker">
                <VField label="Brokerage" value={form.brokerName} onChange={set("brokerName")} source={extracted.brokerName} />
                <VField half label="Contact" value={form.brokerContact} onChange={set("brokerContact")} source={extracted.brokerContact} />
                <VField half label="Phone" value={form.brokerPhone} onChange={set("brokerPhone")} source={extracted.brokerPhone} isMono />
                <div style={{ flex: "1 1 100%" }}>
                  <Micro style={{ marginBottom: 5 }}>How they want updates</Micro>
                  <select value={form.updateVia} onChange={(e) => set("updateVia")(e.target.value)} style={{ ...selectStyle, width: "100%", fontSize: 14, padding: "9px 11px", boxSizing: "border-box" }}>
                    {Object.entries(UPDATE_VIA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </Section>
              <Section title="Driver">
                <select value={form.driverId} onChange={(e) => set("driverId")(e.target.value)} style={{ ...selectStyle, width: "100%", fontSize: 14, padding: "9px 11px", boxSizing: "border-box" }}>
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} — {d.truck}</option>)}
                </select>
              </Section>
              <Section title="Company contact" note="Who Alfred calls when a person has to step in on this load.">
                <ContactPicker value={form.contact1} onChange={set("contact1")} label="Primary" />
                {!form.secondOpen ? (
                  <button onClick={() => setForm((f) => ({ ...f, secondOpen: true }))} style={{ font: "inherit", fontSize: 13.5, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3, textAlign: "left" }}>Add a second contact</button>
                ) : (
                  <div className="alf-fade" style={{ flex: "1 1 100%" }}>
                    <ContactPicker value={form.contact2} onChange={set("contact2")} label="Second contact" allowNone />
                    <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 8 }}>If the primary is off shift, or doesn't pick up within 20 minutes, this contact gets it.</div>
                  </div>
                )}
              </Section>
            </div>
          )}
        </div>

        {step !== "reading" && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "13px 22px", borderTop: `1px solid ${T.hairSoft}`, background: "#fff", borderRadius: "0 0 12px 12px" }}>
            <div>{step === "verify" && <Button kind="quiet" size="sm" onClick={() => { setStep("choose"); setForm(EMPTY_FORM); setExtracted({}); }}>Start over</Button>}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button kind="quiet" onClick={onClose}>Cancel</Button>
              {step === "verify" && <Button disabled={!canSave} onClick={() => canSave && onCreate(form, Object.keys(extracted).length ? "Rate confirmation" : "Manual entry")}>Save and build the call plan</Button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------------- drivers --------------------------------- */

function WellbeingCard({ w, driver, onHandle, compact }) {
  return (
    <div style={{ padding: compact ? "14px 20px" : "15px 20px", borderTop: `1px solid ${T.hairSoft}`, position: "relative", opacity: w.handled ? 0.55 : 1 }}>
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: w.handled ? "transparent" : T.ink }} />
      <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
        {!compact && <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{driver.name}</span>}
        <Soft>{w.signal}</Soft>
        {w.handled && <span style={{ fontSize: 12, color: T.ink3 }}>followed up</span>}
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{w.at}</span>
      </div>
      <div style={{ borderLeft: `2px solid ${T.hair}`, paddingLeft: 12, marginTop: 10 }}>
        <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{w.quote}</div>
        <Micro style={{ marginTop: 5 }}>On the call, {w.loadRef}</Micro>
      </div>
      <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 9 }}>{w.context}</div>
      {!w.handled && (
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <Button size="sm" onClick={() => onHandle(w.id)}>I'll call {driver.name.split(" ")[0]}</Button>
          <Button size="sm" kind="quiet" onClick={() => onHandle(w.id)}>Someone already did</Button>
        </div>
      )}
    </div>
  );
}

/* one driver, everything about them — cues first, then loads, then their calls */
function DriverProfile({ driver, drivers, loads, wellbeing, setWellbeing, setDrivers, onBack, onOpenLoad }) {
  const [openCall, setOpenCall] = useState(null);
  const flags = wellbeing.filter((w) => w.driverId === driver.id);
  const open = flags.filter((w) => !w.handled);
  const mine = loads.filter((l) => l.driverId === driver.id && !CLOSED.includes(l.status));
  const past = loads.filter((l) => l.driverId === driver.id && CLOSED.includes(l.status));
  const calls = CALL_LOG.filter((c) => c.driverId === driver.id);
  const handle = (id) => setWellbeing((xs) => xs.map((x) => (x.id === id ? { ...x, handled: true } : x)));
  const setSpanish = (on) => setDrivers((xs) => xs.map((d) => (d.id === driver.id ? { ...d, spanishOnly: on, language: on ? "Spanish only" : d.baseLanguage || d.language } : d)));
  const setCanRequest = (on) => setDrivers((xs) => xs.map((d) => (d.id === driver.id ? { ...d, canRequest: on } : d)));
  const myErrands = SEED_ERRANDS.filter((e) => e.driverId === driver.id);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <button onClick={onBack} style={{ font: "inherit", fontSize: 13, color: T.ink2, background: "none", border: "none", cursor: "pointer", padding: 0, alignSelf: "flex-start" }}>‹ All drivers</button>

      {/* 1 — anything emotional comes first, before the operational picture */}
      <Panel>
        <PanelHead
          title={open.length ? `Check on ${driver.name.split(" ")[0]} before anything else` : "Nothing flagged right now"}
          note={open.length
            ? "Alfred heard this on a call and stopped there. It doesn't assess anyone, doesn't score anyone, and this never touches a performance record."
            : `Nothing ${driver.name.split(" ")[0]} has said recently was flagged. If something comes up on a call, it appears here first.`}
          right={open.length ? <Tag solid>{open.length} open</Tag> : <Soft>clear</Soft>} />
        {flags.length === 0 && (
          <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3, lineHeight: 1.55 }}>
            No flags on file. Alfred asks how a driver is doing as part of normal calls, never as an assessment.
          </div>
        )}
        {[...flags].sort((a, b) => (a.handled === b.handled ? 0 : a.handled ? 1 : -1)).map((w) => (
          <WellbeingCard key={w.id} w={w} driver={driver} onHandle={handle} compact />
        ))}
      </Panel>

      {/* 2 — who they are and how Alfred talks to them */}
      <Panel>
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "18px 20px 16px", flexWrap: "wrap" }}>
          <span style={{ width: 44, height: 44, borderRadius: 999, border: `1px solid ${T.hair}`, fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {driver.name.split(" ").map((n) => n[0]).join("")}
          </span>
          <span style={{ flex: 1, minWidth: 160 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: -0.5 }}>{driver.name}</span>
              {driver.spanishOnly && <Tag>Spanish only</Tag>}
              {!driver.canRequest && <Soft>no task requests</Soft>}
              {driver.eld.inMotion && <Soft>rolling</Soft>}
            </span>
            <span style={{ display: "block", fontSize: 13, color: T.ink3, marginTop: 3 }}>{driver.truck} · {driver.trailer} · {driver.tz}</span>
          </span>
          <span style={{ textAlign: "right" }}>
            <Micro>First-call connect</Micro>
            <div style={{ fontFamily: mono, fontSize: 22, letterSpacing: -0.8, marginTop: 4 }}>{Math.round(firstRate(driver.attempts) * 100)}%</div>
          </span>
        </div>
        <div style={{ padding: "0 20px 14px" }}><AttemptBar a={driver.attempts} /></div>
        <div style={{ padding: "0 20px 16px" }}><EldLine driver={driver} /></div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>Spanish only</div>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>
              {driver.spanishOnly
                ? "Every call to this driver runs in Spanish end to end — greeting, disclosure, questions, and read-backs. No English fallback."
                : "Alfred opens in English and switches if the driver answers in Spanish."}
            </div>
          </div>
          <Toggle on={!!driver.spanishOnly} onChange={setSpanish} />
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: "14px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>Can give Alfred tasks</div>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>
              {driver.canRequest
                ? `${driver.name.split(" ")[0]} can ask Alfred to make calls on their behalf — parking, scales, road service, shop hours, directions to a gate. Alfred makes the calls and reports back by voice.`
                : `${driver.name.split(" ")[0]} can't assign work to Alfred. If they ask for something on a call, Alfred takes the request down and passes it to the office instead of acting on it.`}
            </div>
            {driver.canRequest && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.hair}` }}>
                <Micro style={{ marginBottom: 6 }}>Still off-limits, whoever asks</Micro>
                <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.55 }}>
                  Spending money, booking anything that costs the carrier, changing an appointment, calling the broker on the driver's behalf, or calling the driver's own contacts. Those come back to a person.
                </div>
              </div>
            )}
            {myErrands.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.hair}` }}>
                <Micro style={{ marginBottom: 6 }}>Recent asks</Micro>
                {myErrands.map((e) => (
                  <div key={e.id} style={{ display: "flex", gap: 10, padding: "3px 0", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12.5 }}>{e.ask}</span>
                    <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3, marginLeft: "auto" }}>{e.at}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Toggle on={!!driver.canRequest} onChange={setCanRequest} />
        </div>

        <div style={{ padding: "6px 20px 18px", borderTop: `1px solid ${T.hairSoft}` }}>
          <Field label="Phone" value={driver.phone} isMono />
          <Field label="Language" value={driver.language} />
          <Field label="Quiet hours" value={`${driver.quiet} ${driver.tz}`} />
          <Field label="Avg. pickup time" value={`${driver.avgAnswerSec}s`} isMono />
          <Field label="Calls handled" value={String(totalAttempts(driver.attempts))} isMono />
          <Field label="ELD" value={`${driver.eld.provider} · ${driver.eld.dutyLeft}h duty left`} />
          <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.hairSoft}` }}>{driver.note}</div>
        </div>
      </Panel>

      {/* 3 — what they're carrying right now */}
      <Panel>
        <PanelHead title="Current load" note={mine.length ? "Open it to see the call plan and stops." : "Nothing assigned right now."} />
        {mine.length === 0 && <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3 }}>Available for dispatch.</div>}
        {mine.map((l) => {
          const due = l.plan.find((s) => s.state === "due");
          const late = lateRisk(l);
          const det = openDetention(l);
          return (
            <button key={l.id} onClick={() => onOpenLoad(l.ref)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "15px 20px", borderTop: `1px solid ${T.hairSoft}`, background: "none", border: "none", borderLeft: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{l.ref}</span>
                  <Chip status={l.status} />
                  {late && <Tag>late risk</Tag>}
                  {det && <Tag solid>detention</Tag>}
                </span>
                <span style={{ display: "block", fontSize: 15, fontWeight: 500, letterSpacing: -0.3, marginTop: 5 }}>{laneLabel(l)}</span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>
                  {due ? `Next: ${due.title}${due.at ? ` at ${due.at}` : ""}` : "Plan complete"} · {l.broker.name}
                </span>
              </span>
              <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
            </button>
          );
        })}
        {past.length > 0 && (
          <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12.5, color: T.ink3 }}>
            Also on file: {past.map((l) => `${l.ref} (${STATUS[l.status].label.toLowerCase()})`).join(", ")}
          </div>
        )}
      </Panel>

      {/* 4 — every call with this driver, without leaving the profile */}
      <Panel>
        <PanelHead title={`Calls with ${driver.name.split(" ")[0]}`}
          note={calls.length ? `${calls.length} call${calls.length === 1 ? "" : "s"} on record. Recording and transcript on each.` : "No calls on record yet."} />
        {calls.length === 0 && <div style={{ padding: "16px 20px 20px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 13, color: T.ink3 }}>Alfred hasn't reached this driver yet.</div>}
        {calls.map((c) => (
          <CallRow key={c.id} c={c} drivers={drivers} loads={loads} showDriver={false}
            open={openCall === c.id} onToggle={() => setOpenCall(openCall === c.id ? null : c.id)} />
        ))}
      </Panel>
    </div>
  );
}

function Drivers({ drivers, setDrivers, loads, wellbeing, setWellbeing, onOpenLoad }) {
  const [profileId, setProfileId] = useState(null);
  const [openErrand, setOpenErrand] = useState("e3");
  const fleetFirst = drivers.reduce((s, d) => s + d.attempts.first, 0) / drivers.reduce((s, d) => s + totalAttempts(d.attempts), 0);
  const repeats = drivers.filter((d) => firstRate(d.attempts) < 0.7);
  const rolling = drivers.filter((d) => d.eld.inMotion).length;
  const openWell = wellbeing.filter((w) => !w.handled);
  const avgPickup = Math.round(drivers.reduce((s, d) => s + d.avgAnswerSec, 0) / drivers.length);
  const handle = (id) => setWellbeing((xs) => xs.map((x) => (x.id === id ? { ...x, handled: true } : x)));

  const profile = drivers.find((d) => d.id === profileId);
  if (profile) {
    return (
      <DriverProfile driver={profile} drivers={drivers} setDrivers={setDrivers} loads={loads}
        wellbeing={wellbeing} setWellbeing={setWellbeing} onBack={() => setProfileId(null)} onOpenLoad={onOpenLoad} />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Panel>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <Metric label="Drivers" value={String(drivers.length)} sub={`${rolling} rolling right now`} />
          <Metric label="First-call connect" value={`${Math.round(fleetFirst * 100)}%`} sub="Fleet average across attempts" />
          <Metric label="Avg. pickup time" value={`${avgPickup}s`} sub="How long before they answer" />
          <Metric label="Needs a check-in" value={String(openWell.length)} sub="Flagged from what drivers said" emphasis />
        </div>
        {repeats.length > 0 && (
          <div style={{ borderTop: `1px solid ${T.hairSoft}`, padding: "13px 20px", fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
            {repeats.map((d) => d.name).join(", ")} {repeats.length === 1 ? "sits" : "sit"} below 70% on the first attempt — every milestone on their loads costs extra calls.
          </div>
        )}
      </Panel>

      <Panel>
        <PanelHead title="Roster" note="Open a driver for their cues, current load, and every call with them." />
        {drivers.map((d) => {
          const assigned = loads.filter((l) => l.driverId === d.id && !CLOSED.includes(l.status));
          const flagged = wellbeing.some((w) => w.driverId === d.id && !w.handled);
          return (
            <button key={d.id} onClick={() => setProfileId(d.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, background: "none", border: "none", borderTop: `1px solid ${T.hairSoft}`, padding: "15px 20px", cursor: "pointer", textAlign: "left", font: "inherit" }}>
              <span style={{ width: 32, height: 32, borderRadius: 999, border: `1px solid ${T.hair}`, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {d.name.split(" ").map((n) => n[0]).join("")}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{d.name}</span>
                  {flagged && <Tag>check in</Tag>}
                  {d.spanishOnly && <Soft>Spanish only</Soft>}
                  {!d.canRequest && <Soft>no task requests</Soft>}
                  {d.eld.inMotion && <Soft>rolling</Soft>}
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 1 }}>
                  {d.truck} · {assigned.length ? assigned.map((l) => l.ref).join(", ") : "Available"} · {d.tz}
                </span>
              </span>
              <span style={{ fontFamily: mono, fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>{Math.round(firstRate(d.attempts) * 100)}%</span>
              <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
            </button>
          );
        })}
      </Panel>

      <Panel>
        <PanelHead title="Who answers, who doesn't" note={`${repeats.length} driver${repeats.length === 1 ? "" : "s"} below 70% on the first attempt.`} />
        <div style={{ padding: "4px 20px 6px", display: "flex", gap: 16 }}>
          <Legend fill={T.ink} label="1st attempt" /><Legend fill="#9E9E9E" label="2nd" /><Legend fill="#DCDCDC" label="3rd or more" />
        </div>
        {[...drivers].sort((a, b) => firstRate(b.attempts) - firstRate(a.attempts)).map((d) => (
          <div key={d.id} style={{ padding: "13px 20px", borderTop: `1px solid ${T.hairSoft}` }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{d.name}</span>
              <span style={{ fontSize: 12.5, color: T.ink3 }}>{d.truck}</span>
              <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{Math.round(firstRate(d.attempts) * 100)}%</span>
            </div>
            <div style={{ marginTop: 9 }}><AttemptBar a={d.attempts} /></div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, gap: 12 }}>
              <span style={{ fontSize: 12, color: T.ink3 }}>{totalAttempts(d.attempts)} calls · picks up in {d.avgAnswerSec}s</span>
              {firstRate(d.attempts) < 0.7 && <span style={{ fontSize: 12 }}>Needs repeat callbacks</span>}
            </div>
          </div>
        ))}
      </Panel>

      <Panel>
        <PanelHead title="Check on a driver"
          note="When a driver says something about how they're holding up, Alfred flags it and tells them a person will follow up. These are the driver's own words — Alfred doesn't assess anyone."
          right={<span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink3 }}>{openWell.length} open</span>} />
        {wellbeing.map((w) => (
          <WellbeingCard key={w.id} w={w} driver={drivers.find((x) => x.id === w.driverId)} onHandle={handle} />
        ))}
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${T.hairSoft}`, fontSize: 12, color: T.ink3, lineHeight: 1.5 }}>
          Admins only. Not part of any driver's performance record.
        </div>
      </Panel>

      <Panel>
        <PanelHead title="Calls Alfred made for drivers"
          note={`Drivers can hand Alfred a call instead of pulling over to make it themselves.${drivers.filter((d) => !d.canRequest).length ? ` ${drivers.filter((d) => !d.canRequest).map((d) => d.name).join(", ")} can't — their requests go to the office.` : ""}`}
          right={<span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink3 }}>{SEED_ERRANDS.length} today</span>} />
        {SEED_ERRANDS.map((e) => {
          const d = drivers.find((x) => x.id === e.driverId);
          const open = openErrand === e.id;
          return (
            <div key={e.id} style={{ borderTop: `1px solid ${T.hairSoft}` }}>
              <button onClick={() => setOpenErrand(open ? null : e.id)} className="alf-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit" }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{e.ask}</span>
                    {e.state === "running" && <Tag solid>calling</Tag>}
                  </span>
                  <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3 }}>{d.name} · {e.calls.length} place{e.calls.length === 1 ? "" : "s"} called</span>
                </span>
                {e.state === "running" && <Waveform active />}
                <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3 }}>{e.at}</span>
                <span style={{ color: T.ink4, fontSize: 13, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
              </button>
              {open && (
                <div className="alf-fade" style={{ padding: "0 20px 16px" }}>
                  {e.calls.map((c, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, padding: "7px 0", borderTop: i === 0 ? "none" : `1px dashed ${T.hairSoft}`, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 500, minWidth: 170 }}>{c.place}</span>
                      <span style={{ fontSize: 13, color: T.ink2, flex: 1 }}>{c.result}</span>
                    </div>
                  ))}
                  {e.reported && <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 9 }}>{e.reported}</div>}
                </div>
              )}
            </div>
          );
        })}
      </Panel>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
   PROTOTYPE BANNER — non-negotiable while this screen runs on seed data.
   Every number below is hard-coded in this file: the FMCSA record, the drivers,
   the loads, the ELD positions, the call transcripts. Nothing reaches the API,
   no call is placed, nothing persists past a refresh. An operator screen that
   looks live and isn't is the exact failure this platform keeps paying for, so
   it says so at the top rather than in a footnote.
   ───────────────────────────────────────────────────────────────────────────── */
function PrototypeBanner({ what }) {
  return (
    <div style={{
      background: "#000", color: "#fff", fontFamily: sans, fontSize: 12.5,
      padding: "7px 22px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
    }}>
      <span style={{
        fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase",
        border: "1px solid #fff", borderRadius: 3, padding: "1px 5px"
      }}>prototype</span>
      <span>{what} Sample data — nothing here is connected to live calls or saved.</span>
      <Link to="/" style={{ marginLeft: "auto", color: "#fff", fontSize: 12.5, textUnderlineOffset: 3 }}>
        ← Back to the dashboard
      </Link>
    </div>
  );
}

/* ----------------------------------- app ----------------------------------- */

export default function AlfredDispatch() {
  const [loads, setLoads] = useState(SEED_LOADS);
  const [drivers, setDrivers] = useState(SEED_DRIVERS);
  const [queue, setQueue] = useState(SEED_QUEUE);
  const [wellbeing, setWellbeing] = useState(SEED_WELLBEING);
  const [seat, setSeat] = useState("s1");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("overview");
  const [sheet, setSheet] = useState(false);
  const [call, setCall] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const driverOf = (l) => drivers.find((d) => d.id === l.driverId);
  const selectedLoad = loads.find((l) => l.id === selected) || null;

  useEffect(() => {
    if (!call) return;
    const timers = [];
    if (call.phase === "dialing") timers.push(setTimeout(() => setCall((c) => c && { ...c, phase: "connected" }), 1400));
    if (call.phase === "connected" && call.lines.length < call.script.length) {
      timers.push(setTimeout(() => setCall((c) => (c ? { ...c, lines: [...c.lines, c.script[c.lines.length]] } : c)), call.lines.length === 0 ? 400 : 1400));
    }
    if (call.phase === "connected" && call.lines.length === call.script.length) timers.push(setTimeout(() => finishCall(), 1500));
    return () => timers.forEach(clearTimeout);
  }, [call]);

  useEffect(() => {
    if (!call) return;
    const id = setInterval(() => setCall((c) => (c ? { ...c, elapsed: c.elapsed + 1 } : c)), 1000);
    return () => clearInterval(id);
  }, [call && call.phase]);

  function callStep(loadId, stepId) {
    const load = loads.find((l) => l.id === loadId);
    const st = load.plan.find((s) => s.id === stepId);
    const drv = driverOf(load);
    if (st.party === "driver" && drv.eld.inMotion) return;
    setCall({
      loadId, stepId, party: st.party, prevStatus: load.status,
      partyName: st.party === "driver" ? drv.name : load.broker.contact,
      phase: "dialing", lines: [], elapsed: 0, script: scriptFor(st.party, st, drv, load),
    });
    setLoads((ls) => ls.map((l) => (l.id === loadId ? { ...l, status: "calling" } : l)));
  }

  function finishCall() {
    setCall((c) => {
      if (!c) return null;
      setLoads((ls) => ls.map((l) => {
        if (l.id !== c.loadId) return l;
        const plan = l.plan.map((s) => (s.id === c.stepId ? { ...s, state: "done", at: nowLabel() } : s));
        const idx = plan.findIndex((s) => s.id === c.stepId);
        const nextIdx = plan.findIndex((s, i) => i > idx && s.state === "scheduled");
        if (nextIdx > -1) plan[nextIdx] = { ...plan[nextIdx], state: "due" };
        const step = l.plan.find((s) => s.id === c.stepId);
        const finishedEmpty = step.id === "empty";
        return {
          ...l,
          status: finishedEmpty ? "empty" : "rolling",
          plan,
          captured: c.party === "driver" ? { location: "Victoria, TX", eta: "8:40 PM" } : l.captured,
          activity: [...l.activity, { t: nowLabel(), head: step.title, body: c.party === "driver" ? "Driver confirmed. Details read back and filed." : `${l.broker.name} updated.` }],
        };
      }));
      return null;
    });
  }

  function endCall() {
    setCall((c) => {
      if (c) setLoads((ls) => ls.map((l) => (l.id === c.loadId ? { ...l, status: c.prevStatus } : l)));
      return null;
    });
  }

  const patchStep = (loadId, stepId, patch) =>
    setLoads((ls) => ls.map((l) => (l.id === loadId ? { ...l, plan: l.plan.map((s) => (s.id === stepId ? { ...s, ...patch } : s)) } : l)));

  const addStep = (loadId, draft) =>
    setLoads((ls) => ls.map((l) => (l.id === loadId ? { ...l, plan: [...l.plan, { id: "x" + Math.random().toString(36).slice(2, 6), party: "driver", extra: true, title: draft.title, detail: "Added by the office.", at: draft.at, state: "due", notes: "", captures: [] }] } : l)));

  const setBrokerPref = (loadId, via) =>
    setLoads((ls) => ls.map((l) => (l.id === loadId ? { ...l, broker: { ...l.broker, updateVia: via } } : l)));

  function relay(loadId, toDriverId) {
    const to = drivers.find((d) => d.id === toDriverId);
    setLoads((ls) => ls.map((l) => {
      if (l.id !== loadId) return l;
      const from = drivers.find((d) => d.id === l.driverId);
      const plan = l.plan.map((s) => (s.state === "done" ? s : { ...s, state: s.state === "due" ? "scheduled" : s.state }));
      const assignIdx = plan.findIndex((s) => s.state !== "done");
      const relayStep = { id: "relay" + Math.random().toString(36).slice(2, 5), party: "driver", extra: true, title: `Relay assignment — ${to.name}`, detail: `Brief ${to.name} on the load from where ${from.name} left it, confirm equipment and the next appointment.`, at: nowLabel(), state: "due", notes: "", captures: [] };
      const next = [...plan.slice(0, assignIdx === -1 ? plan.length : assignIdx), relayStep, ...plan.slice(assignIdx === -1 ? plan.length : assignIdx)];
      return {
        ...l, driverId: toDriverId, plan: next,
        activity: [...l.activity, { t: nowLabel(), head: "Relay", body: `Remaining calls moved from ${from.name} to ${to.name} (${to.truck}).` }],
      };
    }));
  }

  function openLoadByRef(ref) {
    const l = loads.find((x) => x.ref === ref);
    if (l) { setView("loads"); setSelected(l.id); }
  }

  function createLoad(f, source) {
    const id = "l" + Math.random().toString(36).slice(2, 7);
    const stops = [
      { seq: 1, type: "pickup", name: f.puName || "Shipper", city: f.puCity, window: f.puWindow || "—", state: "pending" },
      { seq: 2, type: "drop", name: f.doName || "Consignee", city: f.doCity, window: f.doWindow || "—", state: "pending" },
    ];
    const plan = buildPlan(stops).map((s, i) => ({ ...s, state: i === 0 ? "due" : "scheduled" }));
    setLoads((ls) => [{
      id, ref: f.ref, status: "needs_driver", rate: f.rate, source, stops,
      driverId: f.driverId, contact1: f.contact1, contact2: f.contact2 || null, hoursToNextStop: 0,
      broker: { name: f.brokerName || "—", contact: f.brokerContact || "—", phone: f.brokerPhone || "—", updateVia: f.updateVia, cadence: "Every 4 hours" },
      paperwork: [{ k: "BOL number", v: null }, { k: "Seal number", v: null }, { k: "Weight", v: null }, { k: "POD signer", v: null }],
      captured: null, plan, activity: [],
    }, ...ls]);
    setSheet(false); setView("loads"); setSelected(id);
  }

  const visible = loads.filter((l) => {
    const closed = CLOSED.includes(l.status);
    if (statusFilter === "active" && closed) return false;
    if (statusFilter === "closed" && !closed) return false;
    if (!q.trim()) return true;
    const hay = [l.ref, driverOf(l).name, driverOf(l).truck, l.broker.name, ...l.stops.map((s) => `${s.name} ${s.city}`)].join(" ").toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  const me = staffById(seat);

  return (
    <div className="alf-root" style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        .alf-bar { animation: alfBar 1s ease-in-out infinite; transform-origin: bottom; }
        @keyframes alfBar { 0%,100% { transform: scaleY(.28);} 50% { transform: scaleY(1);} }
        .alf-dot { width:5px; height:5px; border-radius:999px; background:#fff; animation: alfPulse 1.4s ease-in-out infinite; }
        @keyframes alfPulse { 0%,100% { opacity:.3;} 50% { opacity:1;} }
        .alf-fade { animation: alfFade .26s cubic-bezier(.4,0,.2,1); }
        @keyframes alfFade { from { opacity:0; transform: translateY(4px);} to { opacity:1; transform:none;} }
        .alf-rise { animation: alfRise .28s cubic-bezier(.32,.72,0,1); }
        @keyframes alfRise { from { opacity:0; transform: translateY(12px) scale(.99);} to { opacity:1; transform:none;} }
        .alf-row:hover { background:#FAFAFA; }
        .alf-btn { transition: opacity .15s, transform .1s; }
        .alf-btn:active { transform: scale(.975); }
        .alf-close:hover { color:#000; }
        .alf-root input::placeholder, .alf-root textarea::placeholder { color:${T.ink4}; }
        .alf-root input:focus, .alf-root select:focus, .alf-root textarea:focus { border-color:#000 !important; }
        .alf-root button:focus-visible, .alf-root input:focus-visible, .alf-root select:focus-visible, .alf-root textarea:focus-visible { outline:2px solid #000; outline-offset:2px; }
        @media (prefers-reduced-motion: reduce) { .alf-bar,.alf-dot,.alf-fade,.alf-rise { animation:none !important; } }
      `}</style>

      <PrototypeBanner what="Dispatch board." />

      <header style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(255,255,255,0.9)", backdropFilter: "saturate(180%) blur(20px)", borderBottom: `1px solid ${T.hair}` }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 22px", minHeight: 54, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4 }}>Alfred</span>
          <Segmented value={view} onChange={setView} options={[
            { v: "overview", label: "Overview" }, { v: "loads", label: "Loads" },
            { v: "drivers", label: "Drivers" }, { v: "history", label: "History" }, { v: "settings", label: "Settings" }]} />
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {call && <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5 }}><Waveform active /> 1 call live</span>}
            {queue.length > 0 && view !== "overview" && (
              <button onClick={() => setView("overview")} style={{ font: "inherit", fontSize: 12.5, background: T.ink, color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer" }}>
                {queue.length} waiting on a person
              </button>
            )}
            <select value={seat} onChange={(e) => setSeat(e.target.value)} style={selectStyle} title="Who's at the keyboard">
              {SEATS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <Button onClick={() => setSheet(true)}>New load</Button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 22px 60px" }}>
        {view === "overview" && <Overview queue={queue} drivers={drivers} loads={loads} seat={seat} onOpenLoad={openLoadByRef} onResolve={(id) => setQueue((x) => x.filter((y) => y.id !== id))} />}
        {view === "history" && <History drivers={drivers} loads={loads} />}
        {view === "settings" && <Settings />}
        {view === "drivers" && <div style={{ maxWidth: 780 }}><Drivers drivers={drivers} setDrivers={setDrivers} loads={loads} wellbeing={wellbeing} setWellbeing={setWellbeing} onOpenLoad={openLoadByRef} /></div>}

        {view === "loads" && (
          <div style={{ display: "flex", gap: 22, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 400px", minWidth: 300, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "flex", gap: 8, padding: "12px 14px", borderBottom: `1px solid ${T.hairSoft}`, flexWrap: "wrap" }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search load, driver, truck, city, broker"
                  style={{ flex: 1, minWidth: 170, font: "inherit", fontSize: 13, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "7px 10px", outline: "none" }} />
                <Segmented value={statusFilter} onChange={setStatusFilter} options={[{ v: "active", label: "Active" }, { v: "closed", label: "Closed" }, { v: "all", label: "All" }]} />
              </div>
              {visible.length === 0 && <div style={{ padding: "24px 20px", fontSize: 13.5, color: T.ink3 }}>No loads match.</div>}
              {visible.map((l, i) => {
                const d = driverOf(l);
                const isSel = l.id === selected;
                const isLive = call && call.loadId === l.id;
                const flags = queue.filter((x) => x.loadRef === l.ref).length;
                const due = l.plan.find((s) => s.state === "due");
                const det = openDetention(l);
                const late = lateRisk(l);
                return (
                  <button key={l.id} onClick={() => setSelected(l.id)} className="alf-row" style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", border: "none",
                    borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, background: isSel ? "#F0F0F0" : "transparent",
                    cursor: "pointer", textAlign: "left", font: "inherit", position: "relative" }}>
                    <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: isLive || flags ? T.ink : "transparent" }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontFamily: mono, fontSize: 11.5, color: T.ink3, letterSpacing: 0.3 }}>{l.ref}</span>
                        <Chip status={isLive ? "calling" : l.status} />
                        {late && <Tag>late risk</Tag>}
                        {det && <Tag solid>detention</Tag>}
                        {flags > 0 && !isLive && <span style={{ fontSize: 11.5, borderBottom: `1px solid ${T.ink}` }}>{flags} waiting</span>}
                      </span>
                      <span style={{ display: "block", fontSize: 15, fontWeight: 500, letterSpacing: -0.3, marginTop: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{laneLabel(l)}</span>
                      <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {d.name} · {due ? `next: ${due.title}${due.at ? ` at ${due.at}` : ""}` : "plan complete"}
                      </span>
                    </span>
                    <span style={{ color: T.ink4, fontSize: 15 }}>›</span>
                  </button>
                );
              })}
            </div>

            <div style={{ flex: "1 1 400px", minWidth: 300, position: "sticky", top: 80, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, minHeight: 340, maxHeight: "calc(100vh - 110px)", overflow: "hidden" }}>
              <Inspector load={selectedLoad} driver={selectedLoad ? driverOf(selectedLoad) : null} drivers={drivers} call={call}
                onCallStep={callStep} onEndCall={endCall} onPatchStep={patchStep} onAddStep={addStep}
                onSetBrokerPref={setBrokerPref} onRelay={relay} onClose={() => setSelected(null)} />
            </div>
          </div>
        )}
      </main>

      {sheet && <NewLoadSheet onClose={() => setSheet(false)} onCreate={createLoad} drivers={drivers} />}
    </div>
  );
}
